import React, { useEffect, useMemo, useRef, useState } from 'react';
import API from './api';
import QRCode from 'react-qr-code';
import StudentDetailsModal from './StudentDetailsModal';
import {
  MapPin,
  Building2,
  Home,
  Crosshair,
  Car,
  Sparkles,
  Copy,
  Check,
} from 'lucide-react';
import { MapModal } from './MapModal';
import { FaBookOpen, FaRegStickyNote, FaStickyNote } from 'react-icons/fa';
import { SiGooglemaps } from 'react-icons/si';
import { FiVideo, FiVideoOff, FiLink2 } from 'react-icons/fi';
import localforage from 'localforage';

/** =========================================================
 * Feature flag (build-time, React)
 * .env (frontend root):
 *   REACT_APP_OFFLINE_QUEUE=1
 * ========================================================= */
const OFFLINE_QUEUE_ENABLED = process.env.REACT_APP_OFFLINE_QUEUE === '1';

/** =========================================================
 * Offline outbox + lightweight caching (SaaS/pro)
 * ========================================================= */

let storage = null;
try {
  storage = localforage.createInstance({
    name: 'earlycare',
    storeName: 'teacher_app',
  });
} catch {
  storage = null;
}

const OUTBOX_KEY = 'outbox:v1';

function nowIso() {
  return new Date().toISOString();
}

function safeUUID() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {}
  return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function isOfflineQueueOn() {
  return !!OFFLINE_QUEUE_ENABLED && !!storage;
}

async function loadOutbox() {
  if (!isOfflineQueueOn()) return [];
  const q = (await storage.getItem(OUTBOX_KEY)) || [];
  return Array.isArray(q) ? q : [];
}

async function saveOutbox(q) {
  if (!isOfflineQueueOn()) return;
  await storage.setItem(OUTBOX_KEY, q || []);
}

function extractVisitIdFromUrl(url) {
  const m = String(url || '').match(/\/visits\/(\d+)/i);
  return m ? Number(m[1]) : null;
}

function extractWorkIdFromUrl(url) {
  const m = String(url || '').match(/\/teacher-work\/(\d+)/i);
  return m ? Number(m[1]) : null;
}

function computeOutboxStats(queue) {
  const q = Array.isArray(queue) ? queue : [];
  const byVisit = {};
  const byWork = {};
  const byKind = {};
  let failed = 0;

  for (const it of q) {
    const vid = it.visitId ?? extractVisitIdFromUrl(it.url);
    if (vid != null) byVisit[vid] = (byVisit[vid] || 0) + 1;

    const wid = it.workId ?? extractWorkIdFromUrl(it.url);
    if (wid != null) byWork[wid] = (byWork[wid] || 0) + 1;

    const k = it.kind || 'unknown';
    byKind[k] = (byKind[k] || 0) + 1;
    if ((it.retries || 0) >= 1 && it.lastError) failed += 1;
  }

  return {
    pending: q.length,
    failed,
    byVisit,
    byWork,
    byKind,
    oldestAt: q[0]?.createdAt || null,
    headError: q[0]?.lastError || null,
    headRetries: q[0]?.retries || 0,
  };
}

function buildClinicDestFromLocationRow(loc) {
  if (!loc) return null;

  const parts = [loc.street, loc.city, loc.state, loc.zip, loc.country]
    .map((x) => String(x || '').trim())
    .filter(Boolean);

  const addressLine = parts.join(', ');

  const lat = loc.latitude != null ? Number(loc.latitude) : null;
  const lon = loc.longitude != null ? Number(loc.longitude) : null;

  return {
    name: loc.name || 'Clinic',
    address: addressLine,
    lat,
    lon,
    latitude: lat,
    longitude: lon,
  };
}

function getClinicNameForVisit(v, orgLocationsMap) {
  if (!v) return '';
  const mode = String(v.location_mode || '').toLowerCase();
  if (mode !== 'at_clinic') return '';
  const id = Number(v.org_location_id);
  if (!id) return '';
  const loc = orgLocationsMap?.[id];
  return (loc?.name || '').trim();
}

function getOrgLocNameForWork(w, orgLocationsMap) {
  if (!w) return '';
  if (String(w.location_mode || '') !== 'org_location') return '';
  const id = Number(w.org_location_id);
  if (!id) return '';
  const loc = orgLocationsMap?.[id];
  return (loc?.name || '').trim();
}

async function enqueueOutbox(item) {
  if (!isOfflineQueueOn()) {
    throw new Error('OFFLINE_QUEUE_DISABLED');
  }

  const q = await loadOutbox();
  const dedupeKey = item.dedupeKey || null;
  let next = q;

  if (dedupeKey) {
    next = q.filter((x) => x.dedupeKey !== dedupeKey);
  }

  next.push({
    id: safeUUID(),
    createdAt: nowIso(),
    retries: 0,
    lastError: null,
    lastTriedAt: null,
    ...item,
  });

  await saveOutbox(next);
  return next;
}

async function updateOutboxHeadFailure(err) {
  if (!isOfflineQueueOn()) return [];

  const q = await loadOutbox();
  if (!q.length) return q;

  const status = err?.response?.status;
  const msg = status
    ? `HTTP ${status}${err?.response?.data?.error ? `: ${err.response.data.error}` : ''}`
    : 'NETWORK';

  q[0] = {
    ...q[0],
    retries: (q[0].retries || 0) + 1,
    lastError: msg,
    lastTriedAt: nowIso(),
  };

  await saveOutbox(q);
  return q;
}

async function flushOutboxOnce() {
  if (!isOfflineQueueOn()) return { ok: true, flushed: 0 };
  if (!navigator.onLine) return { ok: false, reason: 'offline' };

  let q = await loadOutbox();
  if (!q.length) return { ok: true, flushed: 0 };

  let flushed = 0;

  while (q.length) {
    const it = q[0];

    try {
      await API.request({
        method: it.method,
        url: it.url,
        data: it.data,
        headers: {
          ...(it.headers || {}),
          'X-Client-Request-Id': it.id,
          'X-Offline-Queue': '1',
        },
      });

      q.shift();
      flushed += 1;
      await saveOutbox(q);
    } catch (err) {
      await updateOutboxHeadFailure(err);
      return { ok: false, flushed, reason: 'error', error: err };
    }
  }

  return { ok: true, flushed };
}

async function cacheSet(key, value) {
  if (!isOfflineQueueOn()) return;
  try {
    await storage.setItem(key, { value, cachedAt: nowIso() });
  } catch {}
}

async function cacheGet(key) {
  if (!isOfflineQueueOn()) return null;
  try {
    const obj = await storage.getItem(key);
    return obj?.value ?? null;
  } catch {
    return null;
  }
}

/** =========================================================
 * AI helpers
 * ========================================================= */

function normalizeDomainKey(v) {
  let s = String(v || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  s = s.replace(/^domain_/, '').replace(/_domain$/, '');
  s = s.replace(/^prog_/, '');

  return s;
}

function mergeNoteText(existing, incoming) {
  const a = String(existing || '').trim();
  const b = String(incoming || '').trim();

  if (!b) return a;
  if (!a) return b;
  if (a.includes(b)) return a;

  return `${a}\n\n${b}`;
}

function buildDomainAliasSet(obj) {
  const rawValues = [
    obj?.code,
    obj?.name,
    obj?.label,
    obj?.domain_code,
    obj?.domain_name,
    obj?.domain_label,
  ];

  const normalized = rawValues
    .map((x) => normalizeDomainKey(x))
    .filter(Boolean);

  return Array.from(new Set(normalized));
}

function findBestDomainMatch(highlight, domainList) {
  if (!Array.isArray(domainList) || !domainList.length) return null;

  const highlightDomainId = Number(highlight?.domain_id);
  if (Number.isFinite(highlightDomainId)) {
    const byId = domainList.find((d) => Number(d?.id) === highlightDomainId);
    if (byId) return byId;
  }

  const highlightAliases = buildDomainAliasSet(highlight);
  if (!highlightAliases.length) return null;

  const exactAliasMatch = domainList.find((d) => {
    const domainAliases = buildDomainAliasSet(d);
    return domainAliases.some((alias) => highlightAliases.includes(alias));
  });
  if (exactAliasMatch) return exactAliasMatch;

  const partialAliasMatch = domainList.find((d) => {
    const domainAliases = buildDomainAliasSet(d);
    return domainAliases.some((da) =>
      highlightAliases.some((ha) => da && ha && (da.includes(ha) || ha.includes(da)))
    );
  });
  if (partialAliasMatch) return partialAliasMatch;

  return null;
}

async function copyToClipboardSafe(text) {
  const value = String(text || '');
  if (!value) return false;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {}

  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch {
    return false;
  }
}

function normalizeAiAccessRow(row) {
  return {
    ai_enabled: !!row?.ai_enabled,
    ai_guidance_enabled: !!row?.ai_guidance_enabled,
    ai_notes_enabled: !!row?.ai_notes_enabled,
    ai_nightly_guidance_enabled: !!row?.ai_nightly_guidance_enabled,
    ai_cache_enabled: row?.ai_cache_enabled == null ? true : !!row.ai_cache_enabled,
    ai_tier: row?.ai_tier || 'none',
    ai_daily_limit: row?.ai_daily_limit ?? null,
    ai_monthly_limit: row?.ai_monthly_limit ?? null,
    usage: row?.usage || null,
  };
}

function getAiDisabledMessage(kind) {
  if (kind === 'visit_guidance') {
    return 'AI Guidance is not enabled for your organization.';
  }
  if (kind === 'visit_note') {
    return 'AI Notes is not enabled for your organization.';
  }
  return 'AI is not enabled for your organization.';
}

/** =========================================================
 * MODALS
 * ========================================================= */

function CancelModal({ open, onClose, onSubmit, loading }) {
  const [reason, setReason] = useState('');
  const REASONS = [
    'Cancelled by teacher',
    'Cancelled by family',
    'Late cancellation',
    'NoShow/withAppointmentConfirmation',
  ];

  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
        <h3 className="text-lg font-bold mb-4">Cancel Visit</h3>
        <p className="mb-2">Are you sure you want to cancel this visit?</p>
        <label className="block mb-1 font-semibold">Reason for cancelation:</label>
        <select
          className="border rounded px-3 py-2 w-full mb-4"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        >
          <option value="">-- Choose reason --</option>
          {REASONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <div className="flex justify-end gap-2 mt-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-300 rounded">
            Back
          </button>
          <button
            onClick={() => onSubmit(reason)}
            disabled={!reason || loading}
            className={`px-4 py-2 bg-purple-700 text-white rounded ${
              !reason || loading ? 'opacity-60 pointer-events-none' : ''
            }`}
          >
            {loading ? 'Canceling...' : 'Cancel Visit'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CancelWorkModal({ open, onClose, onSubmit, loading }) {
  const [reason, setReason] = useState('');
  const REASONS = ['Schedule changed', 'Completed elsewhere', 'Other'];

  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
        <h3 className="text-lg font-bold mb-4">Cancel Work</h3>
        <p className="mb-2">Are you sure you want to cancel this work block?</p>
        <label className="block mb-1 font-semibold">Reason:</label>
        <select
          className="border rounded px-3 py-2 w-full mb-4"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        >
          <option value="">-- Choose reason --</option>
          {REASONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>

        <div className="flex justify-end gap-2 mt-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-300 rounded">
            Back
          </button>
          <button
            onClick={() => onSubmit(reason)}
            disabled={!reason || loading}
            className={`px-4 py-2 bg-purple-700 text-white rounded ${
              !reason || loading ? 'opacity-60 pointer-events-none' : ''
            }`}
          >
            {loading ? 'Canceling...' : 'Cancel Work'}
          </button>
        </div>
      </div>
    </div>
  );
}

function WorkLocationModal({ open, onClose, title, addressLine, dest, teacher }) {
  if (!open) return null;

  const hasCoords =
    (dest?.lat != null && dest?.lon != null) ||
    (dest?.latitude != null && dest?.longitude != null);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 className="text-lg font-bold truncate">{title || 'Work location'}</h3>
            {addressLine ? <p className="text-sm text-gray-600 mt-1">{addressLine}</p> : null}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" title="Close">
            ✕
          </button>
        </div>

        {!hasCoords ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            Location coordinates are missing for this work block.
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <MapModal open={open} student={dest} teacher={teacher} onClose={onClose} />
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function WorkNotesModal({ open, onClose, data }) {
  if (!open) return null;

  const teacherName = data?.teacherName || '';
  const workType = data?.workType || '';
  const workTitle = data?.workTitle || '';
  const notes = data?.notes || '';
  const scheduledAt = data?.scheduledAt || '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-5 rounded shadow-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start gap-3 mb-3">
          <div className="min-w-0">
            <h3 className="text-lg font-bold truncate">Work Notes — {workType}</h3>
            <div className="text-xs text-gray-500 mt-1">
              {teacherName ? `Teacher: ${teacherName}` : null}
              {scheduledAt ? ` • ${String(scheduledAt).slice(0, 16).replace('T', ' ')}` : null}
            </div>
          </div>
          <button className="text-gray-500" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="space-y-3">
          <div className="border rounded p-3 bg-gray-50">
            <div className="text-xs text-gray-500 mb-1">Title</div>
            <div className="font-semibold text-slate-800">
              {workTitle || <span className="italic text-gray-400">No title</span>}
            </div>
          </div>

          <div className="border rounded p-3 bg-gray-50">
            <div className="text-xs text-gray-500 mb-1">Notes</div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap">
              {notes || <span className="italic text-gray-400">No notes</span>}
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function StudentReminderModal({
  open,
  onClose,
  studentName,
  value,
  setValue,
  meta,
  loading,
  saving,
  onSave,
  offlineHint,
  offlineQueueEnabled,
}) {
  if (!open) return null;

  const updatedLine = meta?.updated_at
    ? `Last updated: ${String(meta.updated_at).slice(0, 16).replace('T', ' ')}${
        meta?.updated_by_name ? ` by ${meta.updated_by_name}` : ''
      }`
    : '';

  const offlineBlocked = offlineHint && !offlineQueueEnabled;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[85vh] overflow-y-auto p-5">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h3 className="text-lg font-bold">Student reminder</h3>
            <p className="text-sm text-gray-600">{studentName || '—'}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" title="Close">
            ✕
          </button>
        </div>

        {offlineHint ? (
          <div
            className={`mb-3 text-xs rounded px-3 py-2 border ${
              offlineQueueEnabled
                ? 'bg-amber-50 border-amber-200 text-amber-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {offlineQueueEnabled
              ? 'You are offline. Changes will be saved and synced automatically when you are back online.'
              : 'You are offline. Offline save is disabled for your plan—please reconnect to save.'}
          </div>
        ) : null}

        {loading ? (
          <div className="p-4 text-center text-gray-400 text-sm">Loading reminder...</div>
        ) : (
          <>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Shared note for anyone working with this student
            </label>
            <textarea
              className="w-full border rounded px-3 py-2 text-sm"
              rows={7}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Example: Next session focus on joint attention; bring picture cards; ask parent about sleep changes..."
              disabled={offlineBlocked}
            />

            {updatedLine ? (
              <div className="mt-2 text-[11px] text-gray-500">{updatedLine}</div>
            ) : (
              <div className="mt-2 text-[11px] text-gray-400">No reminder set yet.</div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={saving || offlineBlocked}
                className={`px-4 py-2 text-sm rounded text-white ${
                  saving || offlineBlocked
                    ? 'bg-blue-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
                title={offlineBlocked ? 'Offline save disabled' : 'Save'}
              >
                {saving ? 'Saving...' : offlineHint && offlineQueueEnabled ? 'Save (sync later)' : 'Save reminder'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MentorFeedbackModal({ open, onClose, visit, offlineHint, onSaveOffline, offlineQueueEnabled }) {
  const [participation, setParticipation] = useState('observed');
  const [rating, setRating] = useState('3');
  const [mentorLevel, setMentorLevel] = useState('');
  const [suggestPromotion, setSuggestPromotion] = useState('none');
  const [flagged, setFlagged] = useState(false);
  const [notes, setNotes] = useState('');
  const [stageAtVisit, setStageAtVisit] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasExisting, setHasExisting] = useState(false);

  useEffect(() => {
    if (!open) {
      setParticipation('observed');
      setRating('3');
      setMentorLevel('');
      setSuggestPromotion('none');
      setFlagged(false);
      setNotes('');
      setStageAtVisit('');
      setSaving(false);
      setLoading(false);
      setHasExisting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !visit?.id) return;
    if (!navigator.onLine) return;

    setLoading(true);
    setHasExisting(false);

    (async () => {
      try {
        const res = await API.get(`/training/feedback/${visit.id}`);
        if (res.data) {
          const fb = res.data;
          setHasExisting(true);

          if (fb.mentor_rating != null) setRating(String(fb.mentor_rating));
          if (fb.mentor_level) setMentorLevel(fb.mentor_level);
          if (fb.mentor_comment) setNotes(fb.mentor_comment);
          if (fb.suggest_promotion) setSuggestPromotion(fb.suggest_promotion);
          setFlagged(!!fb.is_flagged);
          if (fb.stage_at_visit) setStageAtVisit(fb.stage_at_visit);
        }
      } catch (err) {
        console.error(err);
        const msg = err?.response?.data?.error || 'Failed to load existing mentor feedback.';
        alert(msg);
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [open, visit, onClose]);

  if (!open || !visit) return null;

  const studentName =
    (visit.student_last_name || '') +
    (visit.student_last_name ? ', ' : '') +
    (visit.student_first_name || '');

  const timeRange = visit.scheduled_time
    ? visit.scheduled_time + (visit.event_end_time ? `–${visit.event_end_time.slice(0, 5)}` : '')
    : '';

  const offlineBlocked = offlineHint && !offlineQueueEnabled;

  const handleSave = async () => {
    if (!visit?.id) return;

    const ratingNum = rating ? Number(rating) : null;
    if (rating && (Number.isNaN(ratingNum) || ratingNum < 1 || ratingNum > 5)) {
      alert('Overall rating must be between 1 and 5.');
      return;
    }

    const payload = {
      mentor_rating: ratingNum,
      mentor_level: mentorLevel || null,
      mentor_comment: notes || null,
      suggest_promotion: suggestPromotion || 'none',
      is_flagged: flagged ? 1 : 0,
    };

    if (!navigator.onLine) {
      if (!offlineQueueEnabled) {
        alert('Offline save is disabled. Please reconnect to submit mentor feedback.');
        return;
      }
      setSaving(true);
      try {
        await onSaveOffline?.(visit.id, payload);
        onClose();
      } catch (e) {
        console.error(e);
        alert('Failed to queue mentor feedback.');
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    try {
      await API.post(`/training/feedback/${visit.id}`, payload);
      onClose();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.error || 'Failed to save mentor feedback.';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[80vh] overflow-y-auto p-5">
        <h3 className="text-lg font-bold mb-3">{hasExisting ? 'Edit mentor feedback' : 'Mentor feedback'}</h3>

        <div className="text-sm mb-3 space-y-1">
          <div>
            <span className="font-semibold">Student: </span>
            <span>{studentName || '—'}</span>
          </div>
          <div>
            <span className="font-semibold">Visit time: </span>
            <span>{timeRange || '—'}</span>
          </div>
          <div>
            <span className="font-semibold">Trainee: </span>
            <span>#{visit.trainee_teacher_id} (trainee)</span>
          </div>
          {stageAtVisit && (
            <div>
              <span className="font-semibold">Stage during visit: </span>
              <span>{stageAtVisit}</span>
            </div>
          )}
        </div>

        {offlineHint ? (
          <div
            className={`mb-3 text-xs rounded px-3 py-2 border ${
              offlineQueueEnabled
                ? 'bg-amber-50 border-amber-200 text-amber-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {offlineQueueEnabled
              ? 'You are offline. Your feedback will be saved and synced automatically when you are back online.'
              : 'You are offline. Offline save is disabled—please reconnect to submit feedback.'}
          </div>
        ) : null}

        {loading ? (
          <div className="p-4 text-center text-gray-400 text-sm">Loading existing feedback...</div>
        ) : (
          <>
            <div className="mb-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Participation level (for your reference)
              </label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={participation}
                onChange={(e) => setParticipation(e.target.value)}
                disabled={offlineBlocked}
              >
                <option value="observed">Observed only (shadow)</option>
                <option value="co_led">Co-led with mentor</option>
                <option value="led">Led by trainee</option>
              </select>
              <p className="mt-1 text-[11px] text-gray-500">
                This value is not part of automatic promotion rules, it is just a note for you.
              </p>
            </div>

            <div className="mb-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Overall rating (1–5)</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={rating}
                onChange={(e) => setRating(e.target.value)}
                disabled={offlineBlocked}
              >
                <option value="1">1 – Needs a lot of support</option>
                <option value="2">2 – Emerging skills</option>
                <option value="3">3 – Solid / expected</option>
                <option value="4">4 – Strong performance</option>
                <option value="5">5 – Ready for independent work</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Mentor level (summary)</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={mentorLevel}
                onChange={(e) => setMentorLevel(e.target.value)}
                disabled={offlineBlocked}
              >
                <option value="">-- Choose level --</option>
                <option value="needs_support">Needs support – requires close guidance</option>
                <option value="improving">Improving – progressing but not yet stable</option>
                <option value="almost_independent">Almost independent – needs occasional support</option>
                <option value="ready_for_independent">Ready for independent work</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Promotion suggestion</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={suggestPromotion}
                onChange={(e) => setSuggestPromotion(e.target.value)}
                disabled={offlineBlocked}
              >
                <option value="none">No promotion suggestion</option>
                <option value="to_supervised">Suggest promotion to supervised stage</option>
                <option value="to_independent">Suggest promotion to independent work</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="inline-flex items-center gap-2 text-xs text-red-700">
                <input
                  type="checkbox"
                  className="rounded"
                  checked={flagged}
                  onChange={(e) => setFlagged(e.target.checked)}
                  disabled={offlineBlocked}
                />
                Flag this visit for admin review
              </label>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Mentor feedback</label>
              <textarea
                className="w-full border rounded px-3 py-2 text-sm"
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Key strengths, areas to improve, next steps..."
                disabled={offlineBlocked}
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
                disabled={saving}
              >
                Close
              </button>
              <button
                onClick={handleSave}
                disabled={saving || loading || offlineBlocked}
                className={`px-4 py-2 text-sm rounded text-white ${
                  saving || loading || offlineBlocked
                    ? 'bg-blue-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
                title={offlineBlocked ? 'Offline save disabled' : 'Save'}
              >
                {saving
                  ? 'Saving...'
                  : offlineHint && offlineQueueEnabled
                  ? 'Save (sync later)'
                  : hasExisting
                  ? 'Update feedback'
                  : 'Save feedback'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AiVisitSuggestionPanel({
  loading,
  error,
  result,
  onGenerate,
  onCopyShort,
  onCopyDetailed,
  onApplyHighlights,
  copiedField,
  applyMessage,
  disabled,
  generateLocked,
  aiNotesEnabled,
  aiAccessLoading,
  disabledReason,
}) {
  const generateDisabled = loading || disabled || generateLocked || !aiNotesEnabled || aiAccessLoading;

  return (
    <div className="mb-4 border border-sky-200 rounded-xl bg-sky-50 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-sky-700" />
            <h4 className="text-sm font-bold text-sky-900">AI Visit Note Assistant</h4>
          </div>
          <p className="text-[11px] text-sky-800 mt-1">
            Generates a suggested visit note draft from the current visit context and your draft domain notes.
            Teacher review is still required before final save.
          </p>
          {!aiAccessLoading && !aiNotesEnabled ? (
            <p className="text-[11px] text-amber-700 mt-1 font-semibold">
              {disabledReason || 'AI Notes is not enabled for your organization.'}
            </p>
          ) : null}
        </div>

        <button
          type="button"
          onClick={onGenerate}
          disabled={generateDisabled}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold ${
            generateDisabled
              ? 'bg-sky-200 text-sky-500 cursor-not-allowed'
              : 'bg-sky-600 text-white hover:bg-sky-700'
          }`}
          title={
            aiAccessLoading
              ? 'Loading AI access...'
              : !aiNotesEnabled
              ? disabledReason || 'AI Notes is not enabled for your organization.'
              : generateLocked
              ? 'AI suggestion already generated for this open notes session.'
              : disabled
              ? 'Offline'
              : 'Generate AI suggestion'
          }
        >
          <Sparkles className="w-4 h-4" />
          {loading
            ? 'Generating…'
            : generateLocked
            ? 'AI already generated'
            : aiAccessLoading
            ? 'Checking AI access...'
            : 'Generate AI suggestion'}
        </button>
      </div>

      {error ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      ) : null}

      {applyMessage ? (
        <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          {applyMessage}
        </div>
      ) : null}

      {result ? (
        <div className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div className="border rounded-lg bg-white p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-xs font-semibold text-gray-600">Short note</div>
                <button
                  type="button"
                  onClick={onCopyShort}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
                >
                  {copiedField === 'short' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copiedField === 'short' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">
                {result.short_note || <span className="italic text-gray-400">No short note</span>}
              </div>
            </div>

            <div className="border rounded-lg bg-white p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-xs font-semibold text-gray-600">Detailed note</div>
                <button
                  type="button"
                  onClick={onCopyDetailed}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
                >
                  {copiedField === 'detailed' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copiedField === 'detailed' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">
                {result.detailed_note || <span className="italic text-gray-400">No detailed note</span>}
              </div>
            </div>
          </div>

          <div className="border rounded-lg bg-white p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-xs font-semibold text-gray-600">Domain highlights</div>
              <button
                type="button"
                onClick={onApplyHighlights}
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              >
                Apply to domain notes
              </button>
            </div>

            {!Array.isArray(result.domain_highlights) || result.domain_highlights.length === 0 ? (
              <div className="text-sm text-gray-400 italic">No domain highlights returned.</div>
            ) : (
              <div className="space-y-2">
                {result.domain_highlights.map((item, idx) => (
                  <div key={`${item.domain_code || item.domain_id || 'dom'}_${idx}`} className="border rounded p-2 bg-slate-50">
                    <div className="text-xs font-semibold text-slate-700">
                      {item.domain_name || item.domain_code || `Domain ${item.domain_id || ''}`.trim()}
                    </div>
                    <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{item.summary || '—'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div className="border rounded-lg bg-white p-3">
              <div className="text-xs font-semibold text-gray-600 mb-2">Next step suggestion</div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">
                {result.next_step_suggestion || <span className="italic text-gray-400">No suggestion</span>}
              </div>
            </div>

            <div className="border rounded-lg bg-white p-3">
              <div className="text-xs font-semibold text-gray-600 mb-2">Caution flags</div>
              {!Array.isArray(result.caution_flags) || result.caution_flags.length === 0 ? (
                <div className="text-sm text-gray-400 italic">No caution flags.</div>
              ) : (
                <ul className="space-y-1 text-sm text-slate-800 list-disc pl-4">
                  {result.caution_flags.map((flag, idx) => (
                    <li key={`flag_${idx}`}>{flag}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : (
        !loading && (
          <div className="text-xs text-slate-500">
            No AI suggestion generated yet.
          </div>
        )
      )}
    </div>
  );
}

function AiVisitGuidanceModal({ open, onClose, loading, error, visit, data }) {
  if (!open) return null;

  const studentName = visit
    ? `${visit.student_last_name || ''}${visit.student_last_name ? ', ' : ''}${visit.student_first_name || ''}`.trim()
    : '';

  const result = data?.result || null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[88vh] overflow-y-auto p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-sky-700" />
              AI Visit Guidance
            </h3>
            <p className="text-sm text-gray-600">{studentName || 'Student'}</p>
            <p className="text-[11px] text-gray-500 mt-1">
              Suggested guidance for this visit. Teacher review and clinical judgment are still required.
            </p>
          </div>

          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
            title="Close"
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-center text-gray-400 text-sm">Generating AI visit guidance...</div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : !result ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            No AI guidance available.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="border rounded-lg bg-sky-50 border-sky-200 p-4">
              <div className="text-xs font-semibold text-sky-800 mb-1">Session summary</div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">
                {result.session_summary || <span className="italic text-gray-400">No summary</span>}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="border rounded-lg bg-white p-4">
                <div className="text-xs font-semibold text-gray-600 mb-2">Primary focus</div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap">
                  {result.primary_focus || <span className="italic text-gray-400">No focus returned</span>}
                </div>
              </div>

              <div className="border rounded-lg bg-white p-4">
                <div className="text-xs font-semibold text-gray-600 mb-2">Caregiver coaching</div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap">
                  {result.caregiver_coaching || <span className="italic text-gray-400">No coaching suggestion</span>}
                </div>
              </div>
            </div>

            <div className="border rounded-lg bg-white p-4">
              <div className="text-xs font-semibold text-gray-600 mb-3">Suggested activities / strategies</div>

              {!Array.isArray(result.suggested_activities) || result.suggested_activities.length === 0 ? (
                <div className="text-sm text-gray-400 italic">No suggested activities.</div>
              ) : (
                <div className="space-y-3">
                  {result.suggested_activities.map((item, idx) => (
                    <div key={`ai_activity_${idx}`} className="border rounded-lg p-3 bg-slate-50">
                      <div className="font-semibold text-slate-800 text-sm">
                        {item.title || `Activity ${idx + 1}`}
                      </div>

                      {item.why_it_fits ? (
                        <div className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">
                          <span className="font-semibold">Why it fits: </span>
                          {item.why_it_fits}
                        </div>
                      ) : null}

                      {Array.isArray(item.steps) && item.steps.length > 0 ? (
                        <div className="mt-2">
                          <div className="text-xs font-semibold text-gray-600 mb-1">Steps</div>
                          <ul className="list-disc pl-5 space-y-1 text-sm text-slate-800">
                            {item.steps.map((step, sIdx) => (
                              <li key={`ai_step_${idx}_${sIdx}`}>{step}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="border rounded-lg bg-white p-4">
                <div className="text-xs font-semibold text-gray-600 mb-2">Things to watch</div>
                {!Array.isArray(result.things_to_watch) || result.things_to_watch.length === 0 ? (
                  <div className="text-sm text-gray-400 italic">No specific watch-outs.</div>
                ) : (
                  <ul className="list-disc pl-5 space-y-1 text-sm text-slate-800">
                    {result.things_to_watch.map((item, idx) => (
                      <li key={`watch_${idx}`}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="border rounded-lg bg-white p-4">
                <div className="text-xs font-semibold text-gray-600 mb-2">What to avoid</div>
                {!Array.isArray(result.what_to_avoid) || result.what_to_avoid.length === 0 ? (
                  <div className="text-sm text-gray-400 italic">No specific avoid items.</div>
                ) : (
                  <ul className="list-disc pl-5 space-y-1 text-sm text-slate-800">
                    {result.what_to_avoid.map((item, idx) => (
                      <li key={`avoid_${idx}`}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="border rounded-lg bg-white p-4">
              <div className="text-xs font-semibold text-gray-600 mb-2">Caution flags</div>
              {!Array.isArray(result.caution_flags) || result.caution_flags.length === 0 ? (
                <div className="text-sm text-gray-400 italic">No caution flags.</div>
              ) : (
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-800">
                  {result.caution_flags.map((flag, idx) => (
                    <li key={`flag_${idx}`}>{flag}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/** =========================================================
 * UI helpers
 * ========================================================= */

function addMinutesHHMM(hhmm, mins) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date(2000, 0, 1, h || 0, m || 0, 0);
  d.setMinutes(d.getMinutes() + (mins || 0));
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function VisitTimeType({ scheduled_time, event_end_time }) {
  const from = scheduled_time || '';
  let to = null;

  if (event_end_time) {
    to = event_end_time.slice(0, 5);
  } else if (from) {
    to = addMinutesHHMM(from, 45);
  }

  return (
    <div className="flex flex-col items-end text-right sm:items-start sm:text-left">
      <span className="font-mono text-base">{from && to ? `${from}–${to}` : from || '-'}</span>
    </div>
  );
}

function StatusBadge({ status, children }) {
  let color = 'bg-gray-200 text-gray-700';
  if (status === 'pending') color = 'bg-yellow-100 text-yellow-800';
  if (status === 'accepted') color = 'bg-green-100 text-green-700';
  if (status === 'rejected') color = 'bg-red-100 text-red-700';
  if (status === 'draft') color = 'bg-blue-100 text-blue-700';
  if (status === 'complete') color = 'bg-green-200 text-green-900';
  if (status === 'canceled') color = 'bg-purple-200 text-purple-700';
  return <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${color}`}>{children || status}</span>;
}

function ProgramBadge({ program_type, discipline_code }) {
  const raw = (discipline_code || program_type || '').toString().trim();
  if (!raw) return null;

  const code = raw.replace(/^PROG_/i, '').toUpperCase();

  let cls = 'px-2 py-0.5 rounded text-xs';
  let extra = '';
  let label = code;

  if (code === 'PI') {
    extra = ' bg-orange-100 text-orange-800 border-orange-200';
    label = 'PI';
  } else if (code === 'EI') {
    extra = ' bg-blue-100 text-blue-800 border-blue-200';
    label = 'EI';
  } else if (code === 'ABA') {
    extra = ' bg-purple-100 text-purple-800 border-purple-200';
    label = 'ABA';
  } else if (code === 'OT') {
    extra = ' bg-teal-100 text-teal-800 border-teal-200';
    label = 'OT';
  } else if (code === 'PT') {
    extra = ' bg-emerald-100 text-emerald-800 border-emerald-200';
    label = 'PT';
  } else if (code === 'ST') {
    extra = ' bg-green-100 text-green-800 border-green-200';
    label = 'ST';
  } else {
    extra = ' border-gray-400 text-gray-700 bg-gray-50';
    label = code;
  }

  return <span className={cls + extra}>{label}</span>;
}

function EventBadge({ isEvent }) {
  if (!isEvent) return null;
  return (
    <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800 border border-yellow-300">
      EVENT
    </span>
  );
}

function TelehealthBadge({ delivery_mode }) {
  if (delivery_mode !== 'telehealth' && delivery_mode !== 'hybrid') return null;
  const label = delivery_mode === 'telehealth' ? 'Telehealth session' : 'Hybrid (Telehealth)';
  return (
    <span className="inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-sky-100 text-sky-800 border border-sky-300">
      <FiVideo className="w-3 h-3" />
      {label}
    </span>
  );
}

function TrainingBadge({ trainingType }) {
  if (!trainingType) return null;

  let label = null;
  if (trainingType === 'shadow') label = 'Shadow visit';
  else if (trainingType === 'supervised') label = 'Supervised visit';
  else if (trainingType === 'training') label = 'Supervised visit';

  if (!label) return null;

  return (
    <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-teal-100 text-teal-800 border border-teal-300">
      {label}
    </span>
  );
}

function parseDateStr(v) {
  const s = String(v || '');
  const d = s.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : '';
}

function parseHHMM(val) {
  const s = String(val || '').trim();
  const m = s.match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
}

function minutesFromHHMM(hhmm) {
  const t = parseHHMM(hhmm);
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function itemTimeHHMM(v) {
  return parseHHMM(v?.scheduled_time) || parseHHMM(v?.from_hhmm) || parseHHMM(v?.scheduled_at) || '';
}

function itemSortValue(v) {
  const d = parseDateStr(v?.scheduled_at);
  const hhmm = itemTimeHHMM(v);
  const mins = minutesFromHHMM(hhmm) ?? 0;

  const y = Number(d.slice(0, 4)) || 0;
  const mo = Number(d.slice(5, 7)) || 0;
  const da = Number(d.slice(8, 10)) || 0;
  const dayKey = y * 372 + mo * 31 + da;

  return dayKey * 1440 + mins;
}

function sortUnified(a, b) {
  const av = itemSortValue(a);
  const bv = itemSortValue(b);
  if (av !== bv) return av - bv;

  const ak = isWorkItem(a) ? 1 : 0;
  const bk = isWorkItem(b) ? 1 : 0;
  if (ak !== bk) return ak - bk;

  return Number(a?.id || 0) - Number(b?.id || 0);
}

function groupByDate(items) {
  const acc = {};
  for (const v of items || []) {
    const date = parseDateStr(v?.scheduled_at);
    if (!date) continue;
    if (!acc[date]) acc[date] = [];
    acc[date].push(v);
  }

  Object.keys(acc).forEach((d) => acc[d].sort(sortUnified));
  return acc;
}

function normalizeHHMM(s) {
  const t = String(s || '').trim();
  if (!t) return '';
  const m = t.match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
}

function isWorkItem(v) {
  return v && (v.__kind === 'work' || v.kind === 'work' || v._is_work === true);
}

function getRowClass(v, serverDateStr) {
  const visitDateStr = String(v.scheduled_at || '').slice(0, 10);
  const isCanceled = v.status === 'canceled';

  let rowClass = 'hover:bg-gray-50';
  let hideActions = false;

  const work = isWorkItem(v);

  if (isCanceled) {
    rowClass += ' bg-purple-100 opacity-80';
    hideActions = true;
  } else {
    if (visitDateStr < serverDateStr && v.status === 'draft') {
      hideActions = true;
      rowClass += ' opacity-60 bg-gray-100';
    } else if (visitDateStr > serverDateStr) {
      hideActions = true;
      rowClass += ' opacity-80 bg-yellow-50';
    } else if (visitDateStr === serverDateStr) {
      rowClass += ' bg-green-50';
    }
  }

  if (work && !isCanceled && !(visitDateStr < serverDateStr && v.status === 'draft')) {
    rowClass += ' bg-sky-50';
  }

  const reassigned = !work && v.teacher_id !== v.original_teacher_id;
  const status = !work ? (v.reassigned_status || '') : '';
  if (!work && !isCanceled && reassigned && (status === 'pending' || status === 'rejected')) {
    hideActions = true;
    rowClass += status === 'rejected' ? ' bg-red-50' : ' bg-orange-50';
  }

  return { rowClass, hideActions, reassigned, status, visitDateStr, isCanceled };
}

/** =========================================================
 * TRAVEL / TRIP tracking helpers
 * ========================================================= */

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function haversineMeters(lat1, lon1, lat2, lon2) {
  const toRad = (x) => (Number(x) * Math.PI) / 180;
  const R = 6371000;
  const p1 = toRad(lat1);
  const p2 = toRad(lat2);
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(p1) * Math.cos(p2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/** =========================================================
 * WEATHER helpers
 * ========================================================= */

const W2U_SD_URL = 'https://weather2umbrella.com/san-diego,32.7153,-117.156';

let opsWeatherCtx = null;
try {
  // eslint-disable-next-line
  opsWeatherCtx = require.context('./opsWeather', false, /\.(png|jpg|jpeg|svg)$/);
} catch {
  opsWeatherCtx = null;
}

function opsWeatherIconSrc(filename) {
  try {
    if (!opsWeatherCtx || !filename) return null;
    return opsWeatherCtx(`./${filename}`);
  } catch {
    return null;
  }
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickNumber(obj, keys) {
  for (const k of keys) {
    const n = toNum(obj?.[k]);
    if (n != null) return n;
  }
  return null;
}

function normalizeIconFilename(raw) {
  if (!raw) return null;
  let s = String(raw).trim();
  if (!s) return null;
  s = s.replace(/^"+|"+$/g, '');
  s = s.split('/').pop();
  if (!/\.(png|jpg|jpeg|svg)$/i.test(s)) s = `${s}.png`;
  return s;
}

function getAlertCode(weather) {
  const iconRaw = String(weather?.icon || '').trim();
  const iconSuffix = iconRaw ? iconRaw.replace(/\.(png|jpg|jpeg|svg)$/i, '').substring(1) : '';

  const pcp = pickNumber(weather, ['pcp', 'precip', 'precip_mm', 'rain', 'rain_mm']) ?? 0;
  const tmp = pickNumber(weather, ['tmp', 'temp', 'temperature', 't']) ?? 0;
  const ws = pickNumber(weather, ['ws', 'wind', 'wind_speed', 'wind_mps']) ?? 0;
  const rh = pickNumber(weather, ['rh', 'humidity', 'hum']) ?? 0;
  const uv = pickNumber(weather, ['uv', 'uvi']) ?? 0;

  if (iconSuffix === '11' && pcp > 0.8) return 'a04b';
  if (iconSuffix === '13' && pcp > 0.6) return 'a05';
  if ((iconSuffix === '14' || iconRaw.trim() === '15') && tmp < -1) return 'a05';
  if (pcp > 1.5 && tmp > 1.1) return 'a03';
  if (ws > 10) return 'a02';
  if (tmp > 32 && rh > 39) return 'a06';
  if (tmp > 37 && uv > 6.5) return 'a06';
  if (tmp > 36 && uv > 7.0) return 'a01';
  if (tmp > 33 && uv > 9) return 'a01';
  if (uv > 9.0) return 'a01';
  if (tmp < -1 && pcp > 0.5) return 'a04';
  if (ws > 7 && pcp > 1.2) return 'a05';
  return null;
}

const temperatureRangesLight = [
  { range: [-Infinity, -42], color: 'B224B2' },
  { range: [-41, -40], color: 'A724B2' },
  { range: [-39, -35], color: '9B24B2' },
  { range: [-34, -30], color: '8F24B2' },
  { range: [-29, -28], color: '7E24B2' },
  { range: [-27, -26], color: '6D24B2' },
  { range: [-25, -24], color: '5D24B2' },
  { range: [-23, -22], color: '4C24B2' },
  { range: [-21, -20], color: '432DB2' },
  { range: [-19, -18], color: '332DB2' },
  { range: [-17, -16], color: '2D36B2' },
  { range: [-15, -14], color: '2D45B2' },
  { range: [-13, -12], color: '305BBF' },
  { range: [-11, -10], color: '2666BF' },
  { range: [-9, -8], color: '2678BF' },
  { range: [-7, -6], color: '268ABF' },
  { range: [-5, -4], color: '269CBF' },
  { range: [-3, -2], color: '24A2B2' },
  { range: [-1, 0], color: '21A6A6' },
  { range: [1, 2], color: '21A68B' },
  { range: [3, 4], color: '21A671' },
  { range: [5, 6], color: '21A656' },
  { range: [7, 8], color: '21A63C' },
  { range: [9, 10], color: '21A621' },
  { range: [11, 12], color: '3CA621' },
  { range: [13, 14], color: '4CA611' },
  { range: [15, 16], color: '6AA611' },
  { range: [17, 18], color: '89AB00' },
  { range: [19, 20], color: 'B8B800' },
  { range: [21, 22], color: 'CCA300' },
  { range: [23, 24], color: 'CC7E0A' },
  { range: [25, 26], color: 'CC5E14' },
  { range: [27, 28], color: 'CC3914' },
  { range: [29, 30], color: 'BF1D1D' },
  { range: [31, 32], color: 'C4142B' },
  { range: [33, 34], color: 'C71444' },
  { range: [35, 36], color: 'C9145D' },
  { range: [37, 38], color: 'CC1476' },
  { range: [39, 40], color: 'CC148F' },
  { range: [41, 45], color: 'CC149E' },
  { range: [46, Infinity], color: 'CC14AD' },
];

function getTemperatureColorCelsius(tempC) {
  const t = toNum(tempC);
  if (t == null) return '#111827';
  for (const { range, color } of temperatureRangesLight) {
    if (t >= range[0] && t <= range[1]) return `#${color}`;
  }
  return '#111827';
}

function cToF(tempC) {
  const t = toNum(tempC);
  if (t == null) return null;
  return (t * 9) / 5 + 32;
}

function formatF(tempC) {
  const f = cToF(tempC);
  if (f == null) return '—';
  return `${Math.round(f)}°F`;
}

function parseHourLabel(h) {
  if (!h) return null;
  const s = String(h);

  const m1 = s.match(/T(\d{2}):/);
  if (m1 && m1[1]) return `${m1[1]}h`;

  const m2 = s.match(/\s(\d{2}):/);
  if (m2 && m2[1]) return `${m2[1]}h`;

  const n = Number(s);
  if (Number.isFinite(n)) return `${String(n).padStart(2, '0')}h`;

  return null;
}

/** =========================================================
 * MAIN
 * ========================================================= */

function MyVisitsToday() {
  const [currentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  });

  const currentTeacherId = currentUser?.id;
  const trainingStage = (currentUser?.training_stage || 'none').toString().trim().toLowerCase();
  const offlineQueueEnabled = isOfflineQueueOn();

  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [syncing, setSyncing] = useState(false);
  const [outboxStats, setOutboxStats] = useState({
    pending: 0,
    failed: 0,
    byVisit: {},
    byWork: {},
    byKind: {},
    oldestAt: null,
    headError: null,
    headRetries: 0,
  });
  const [justSyncedMsg, setJustSyncedMsg] = useState('');

  const justSyncedTimerRef = useRef(null);

  const [tripToast, setTripToast] = useState('');
  const tripToastTimerRef = useRef(null);

  const [visits, setVisits] = useState([]);
  const [teacherWork, setTeacherWork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState({ lat: null, lon: null });

  const [qrModalVisitId, setQrModalVisitId] = useState(null);
  const [visitConfirmed, setVisitConfirmed] = useState(false);

  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesModalVisit, setNotesModalVisit] = useState(null);
  const [notesEditMode, setNotesEditMode] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);

  const [domainList, setDomainList] = useState([]);
  const [domainNotes, setDomainNotes] = useState({});
  const [notesModalLoading, setNotesModalLoading] = useState(false);

  const [aiNoteLoading, setAiNoteLoading] = useState(false);
  const [aiNoteError, setAiNoteError] = useState('');
  const [aiNoteResult, setAiNoteResult] = useState(null);
  const [aiCopiedField, setAiCopiedField] = useState('');
  const [aiApplyMessage, setAiApplyMessage] = useState('');
  const [aiNoteGeneratedOnce, setAiNoteGeneratedOnce] = useState(false);

  const [aiGuidanceModal, setAiGuidanceModal] = useState({
    open: false,
    loading: false,
    visit: null,
    data: null,
    error: '',
  });

  const [aiAccess, setAiAccess] = useState({
    loading: true,
    loaded: false,
    ai_enabled: false,
    ai_guidance_enabled: false,
    ai_notes_enabled: false,
    ai_nightly_guidance_enabled: false,
    ai_cache_enabled: true,
    ai_tier: 'none',
    usage: null,
  });

  const [serverDateStr, setServerDateStr] = useState(null);
  const [serverTimeStr, setServerTimeStr] = useState(null);
  const [serverTz, setServerTz] = useState(null);

  const [studentDetailsModal, setStudentDetailsModal] = useState({ open: false, student: null });

  const [cancelVisitId, setCancelVisitId] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [cancelWorkId, setCancelWorkId] = useState(null);
  const [cancelWorkLoading, setCancelWorkLoading] = useState(false);

  const [mapModal, setMapModal] = useState({ open: false, student: null, teacher: null });

  const [workLocModal, setWorkLocModal] = useState({
    open: false,
    title: '',
    addressLine: '',
    dest: null,
    teacher: null,
  });

  const [workNotesModal, setWorkNotesModal] = useState({
    open: false,
    teacherName: '',
    workType: '',
    workTitle: '',
    notes: '',
    scheduledAt: '',
  });

  const [showNotesHistory, setShowNotesHistory] = useState(false);
  const [notesHistory, setNotesHistory] = useState([]);
  const [notesHistoryLoading, setNotesHistoryLoading] = useState(false);
  const [notesHistoryStudent, setNotesHistoryStudent] = useState(null);

  const [mentorFeedback, setMentorFeedback] = useState({ open: false, visit: null });

  const [reminderCache, setReminderCache] = useState({});
  const [reminderModal, setReminderModal] = useState({ open: false, student_id: null, student_name: '' });
  const [reminderValue, setReminderValue] = useState('');
  const [reminderMeta, setReminderMeta] = useState(null);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderSaving, setReminderSaving] = useState(false);

  const [opsWeather, setOpsWeather] = useState(null);
  const [opsWeatherLoading, setOpsWeatherLoading] = useState(false);

  const REMINDER_LINE_MAX = 25;
  const REMINDER_PREFIX = 'Reminder: ';

  const pendingByVisit = offlineQueueEnabled ? outboxStats.byVisit || {} : {};
  const pendingByWork = offlineQueueEnabled ? outboxStats.byWork || {} : {};

  const offlineBlocked = !isOnline && !offlineQueueEnabled;
  const [orgLocationsMap, setOrgLocationsMap] = useState({});

  const [trip, setTrip] = useState({
    active: false,
    trip_id: null,
    dest_kind: null,
    dest_id: null,
    destLabel: '',
    destLat: null,
    destLon: null,
    startedAt: null,
    lastPingAt: null,
    lastError: null,
    paused: false,
    destAddressLine: null,
    destSource: null,
  });

  const tripWatchIdRef = useRef(null);
  const lastSentAtRef = useRef(0);
  const lastSentPosRef = useRef({ lat: null, lon: null });
  const tripForceTimerRef = useRef(null);
  const arrivedPromptAtRef = useRef(0);
  const tripRef = useRef(trip);
  const wakeLockRef = useRef(null);

  useEffect(() => {
    tripRef.current = trip;
  }, [trip]);

  const canUseAiGuidance = aiAccess.ai_enabled && aiAccess.ai_guidance_enabled;
  const canUseAiNotes = aiAccess.ai_enabled && aiAccess.ai_notes_enabled;

  const showTripToast = (msg) => {
    setTripToast(msg || '');
    if (tripToastTimerRef.current) clearTimeout(tripToastTimerRef.current);
    tripToastTimerRef.current = setTimeout(() => setTripToast(''), 2800);
  };

  const refreshOutboxStats = async () => {
    if (!offlineQueueEnabled) {
      setOutboxStats({
        pending: 0,
        failed: 0,
        byVisit: {},
        byWork: {},
        byKind: {},
        oldestAt: null,
        headError: null,
        headRetries: 0,
      });
      return;
    }
    const q = await loadOutbox();
    setOutboxStats(computeOutboxStats(q));
  };

  const flushNow = async (opts = { silent: false }) => {
    if (!offlineQueueEnabled) return;
    if (syncing) return;
    if (!navigator.onLine) {
      if (!opts.silent) alert('You are offline. Changes will sync when back online.');
      return;
    }

    setSyncing(true);
    try {
      const before = (await loadOutbox()).length;
      const res = await flushOutboxOnce();
      await refreshOutboxStats();

      if (res?.flushed > 0) {
        setJustSyncedMsg(`Synced ${res.flushed} change${res.flushed === 1 ? '' : 's'}.`);
        if (justSyncedTimerRef.current) clearTimeout(justSyncedTimerRef.current);
        justSyncedTimerRef.current = setTimeout(() => setJustSyncedMsg(''), 3000);
      } else if (!opts.silent && before > 0 && !res.ok) {
        alert('Sync failed. Please check your connection or re-login.');
      }
    } finally {
      setSyncing(false);
    }
  };

  const truncateHard = (txt, maxLen) => {
    const s = (txt || '').toString().trim();
    if (!s || maxLen <= 0) return '';
    if (s.length <= maxLen) return s;
    if (maxLen === 1) return '…';
    return s.slice(0, Math.max(0, maxLen - 1)).trimEnd() + '…';
  };

  const reminderPreview25 = (studentId) => {
    const raw = reminderCache?.[studentId]?.reminder_text || '';
    const maxRest = Math.max(0, REMINDER_LINE_MAX - REMINDER_PREFIX.length);
    return truncateHard(raw, maxRest);
  };

  const hasReminder = (studentId) => {
    const r = reminderCache?.[studentId];
    return !!(r?.reminder_text && String(r.reminder_text).trim().length > 0);
  };

  const isEventVisit = (v) =>
    v.type === 'event' || v.visit_type === 'event' || v.is_event === 1 || v.is_event === true;

  const isTelehealthVisit = (v) =>
    v.delivery_mode === 'telehealth' || v.delivery_mode === 'hybrid';

  const canShowAiGuidanceForVisit = (visit) =>
    !!visit && String(visit?.status || '').toLowerCase() === 'draft';

  const resetAiSuggestionState = () => {
    setAiNoteLoading(false);
    setAiNoteError('');
    setAiNoteResult(null);
    setAiCopiedField('');
    setAiApplyMessage('');
    setAiNoteGeneratedOnce(false);
  };

  const loadAiAccess = async () => {
    const cacheKey = 'cache:ai_access';

    if (!navigator.onLine) {
      if (offlineQueueEnabled) {
        const cached = await cacheGet(cacheKey);
        if (cached) {
          setAiAccess({
            loading: false,
            loaded: true,
            ...normalizeAiAccessRow(cached),
          });
          return;
        }
      }

      setAiAccess((prev) => ({
        ...prev,
        loading: false,
        loaded: false,
      }));
      return;
    }

    setAiAccess((prev) => ({ ...prev, loading: true }));

    try {
      const res = await API.get('/ai/access');
      const row = normalizeAiAccessRow(res?.data?.row || {});
      setAiAccess({
        loading: false,
        loaded: true,
        ...row,
      });
      await cacheSet(cacheKey, row);
    } catch (err) {
      console.error('Failed to load AI access:', err);

      if (offlineQueueEnabled) {
        const cached = await cacheGet(cacheKey);
        if (cached) {
          setAiAccess({
            loading: false,
            loaded: true,
            ...normalizeAiAccessRow(cached),
          });
          return;
        }
      }

      setAiAccess({
        loading: false,
        loaded: false,
        ai_enabled: false,
        ai_guidance_enabled: false,
        ai_notes_enabled: false,
        ai_nightly_guidance_enabled: false,
        ai_cache_enabled: true,
        ai_tier: 'none',
        usage: null,
      });
    }
  };

  const openAiGuidanceModal = async (visit) => {
    if (!visit?.id) return;

    if (aiAccess.loading) {
      setAiGuidanceModal({
        open: true,
        loading: false,
        visit,
        data: null,
        error: 'Checking AI access settings...',
      });
      return;
    }

    if (!canUseAiGuidance) {
      setAiGuidanceModal({
        open: true,
        loading: false,
        visit,
        data: null,
        error: getAiDisabledMessage('visit_guidance'),
      });
      return;
    }

    setAiGuidanceModal({
      open: true,
      loading: true,
      visit,
      data: null,
      error: '',
    });

    if (!navigator.onLine) {
      setAiGuidanceModal({
        open: true,
        loading: false,
        visit,
        data: null,
        error: 'AI visit guidance requires an online connection.',
      });
      return;
    }

    try {
      const res = await API.get(`/ai/visit-guidance/${visit.id}`);
      setAiGuidanceModal({
        open: true,
        loading: false,
        visit,
        data: res?.data || null,
        error: '',
      });
    } catch (err) {
      console.error('AI visit guidance error:', err);
      setAiGuidanceModal({
        open: true,
        loading: false,
        visit,
        data: null,
        error:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Failed to load AI visit guidance.',
      });
    }
  };

  const closeAiGuidanceModal = () => {
    setAiGuidanceModal({
      open: false,
      loading: false,
      visit: null,
      data: null,
      error: '',
    });
  };

  async function requestWakeLock() {
    try {
      if (!('wakeLock' in navigator)) return;
      if (wakeLockRef.current) return;

      const lock = await navigator.wakeLock.request('screen');
      wakeLockRef.current = lock;

      lock.addEventListener('release', () => {
        wakeLockRef.current = null;
      });
    } catch {}
  }

  async function releaseWakeLock() {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch {}
  }

  useEffect(() => {
    const onOnline = async () => {
      setIsOnline(true);
      await refreshOutboxStats();
      await loadAiAccess();
      if (offlineQueueEnabled) {
        if ((await loadOutbox()).length) flushNow({ silent: true });
      }
    };

    const onOffline = async () => {
      setIsOnline(false);
      await refreshOutboxStats();
      await loadAiAccess();
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    refreshOutboxStats();
    loadAiAccess();

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      if (justSyncedTimerRef.current) clearTimeout(justSyncedTimerRef.current);
      if (tripToastTimerRef.current) clearTimeout(tripToastTimerRef.current);

      try {
        if (tripWatchIdRef.current != null && navigator.geolocation) {
          navigator.geolocation.clearWatch(tripWatchIdRef.current);
        }
      } catch {}
      tripWatchIdRef.current = null;

      try {
        if (tripForceTimerRef.current) clearInterval(tripForceTimerRef.current);
      } catch {}
      tripForceTimerRef.current = null;

      try {
        if (wakeLockRef.current) {
          wakeLockRef.current.release();
        }
      } catch {}
      wakeLockRef.current = null;
    };
    // eslint-disable-next-line
  }, [offlineQueueEnabled]);

  useEffect(() => {
    const onVis = () => {
      const t = tripRef.current;
      if (!document.hidden && t?.active && !t?.paused) {
        requestWakeLock();
      }
      if (document.hidden) {
        releaseWakeLock();
      }
    };

    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      (err) => console.warn(err),
      { enableHighAccuracy: true }
    );
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await API.get('/server-time');
        setServerTimeStr(res.data.serverTime);
        setServerDateStr(res.data.serverTime.slice(0, 10));
        setServerTz(res.data.timezone || null);
      } catch {
        const local = new Date().toISOString().slice(0, 19);
        setServerTimeStr(local);
        setServerDateStr(local.slice(0, 10));
      }
    })();
  }, []);

  useEffect(() => {
    if (!navigator.onLine) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await API.get('/travel/active');
        if (cancelled) return;

        const a = res?.data || {};
        if (a.active && a.trip_id) {
          setTrip((p) => ({
            ...(p || {}),
            active: true,
            trip_id: a.trip_id,
            dest_kind: a.dest_kind,
            dest_id: a.dest_id,
            destLabel:
              a.dest_label ||
              p?.destLabel ||
              `${a.dest_kind === 'work' ? 'Work' : 'Visit'} #${a.dest_id}`,
            destLat: a.dest_lat != null ? Number(a.dest_lat) : p?.destLat ?? null,
            destLon: a.dest_lon != null ? Number(a.dest_lon) : p?.destLon ?? null,
            startedAt: a.started_at ? String(a.started_at) : nowIso(),
            destAddressLine: a.dest_address_line || null,
            destSource: a.dest_source || null,
          }));
        } else {
          setTrip((p) =>
            p?.active
              ? {
                  active: false,
                  trip_id: null,
                  dest_kind: null,
                  dest_id: null,
                  destLabel: '',
                  destLat: null,
                  destLon: null,
                  startedAt: null,
                  lastPingAt: null,
                  lastError: null,
                  paused: false,
                  destAddressLine: null,
                  destSource: null,
                }
              : p
          );
        }
      } catch {}
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!navigator.onLine) return;

    (async () => {
      try {
        const res = await API.get('/org-locations');
        const rows = res.data?.locations || [];
        const m = {};
        rows.forEach((r) => {
          if (r?.id) m[Number(r.id)] = r;
        });
        setOrgLocationsMap(m);
      } catch {
        setOrgLocationsMap({});
      }
    })();
  }, []);

  const stopTripInternal = async (reason = 'manual_stop') => {
    const t = tripRef.current;
    if (!t?.active || !t.trip_id) {
      setTrip((p) => ({ ...(p || {}), active: false, trip_id: null }));
      return;
    }

    try {
      if (tripWatchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(tripWatchIdRef.current);
      }
    } catch {}
    tripWatchIdRef.current = null;

    try {
      if (tripForceTimerRef?.current) clearInterval(tripForceTimerRef.current);
    } catch {}
    if (tripForceTimerRef) tripForceTimerRef.current = null;

    try {
      if (navigator.onLine) {
        await API.post('/travel/stop', {
          trip_id: t.trip_id,
          dest_kind: t.dest_kind,
          dest_id: t.dest_id,
          lat: location?.lat ?? null,
          lon: location?.lon ?? null,
          client_time: new Date().toISOString().slice(0, 19).replace('T', ' '),
          stop_reason: reason,
        });
      }
    } catch {}

    setTrip({
      active: false,
      trip_id: null,
      dest_kind: null,
      dest_id: null,
      destLabel: '',
      destLat: null,
      destLon: null,
      startedAt: null,
      lastPingAt: null,
      lastError: null,
      paused: false,
      destAddressLine: null,
      destSource: null,
    });
    await releaseWakeLock();

    lastSentAtRef.current = 0;
    arrivedPromptAtRef.current = 0;

    try {
      if (lastSentPosRef) lastSentPosRef.current = { lat: null, lon: null };
    } catch {}
  };

  const toggleTripPause = () => {
    setTrip((p) => {
      const nextPaused = !p?.paused;
      if (nextPaused) releaseWakeLock();
      else requestWakeLock();
      return { ...(p || {}), paused: nextPaused };
    });
  };

  const confirmSwitchTripIfNeeded = async ({ dest_kind, dest_id, destLabel }) => {
    const t = tripRef.current;
    if (!t?.active || !t?.trip_id) return true;

    if (String(t.dest_kind) === String(dest_kind) && Number(t.dest_id) === Number(dest_id)) {
      return true;
    }

    const current = t.destLabel || `${t.dest_kind}:${t.dest_id}`;
    const next = destLabel || `${dest_kind}:${dest_id}`;

    const ok = window.confirm(
      `Travel tracking is already ON.\n\nCurrent: ${current}\nSwitch to: ${next}\n\nSwitch tracking destination?`
    );

    if (!ok) return false;

    await stopTripInternal('switched');
    return true;
  };

  async function confirmStartTrip({ label }) {
    const ok = window.confirm(
      `Start travel tracking for:\n\n${label || 'this destination'}\n\n` +
        `Info:\n` +
        `• ⚠ IMPORTANT: Locking or sleeping the device may pause GPS tracking.\n` +
        `• You can stop tracking anytime using Stop (or Pause).\n` +
        `• Tracking will also stop automatically when you tap Check-in.\n` +
        `• If you need tracking again after Checkout, start a new trip by tapping the Trip icon.\n\n` +
        `Continue?`
    );
    return !!ok;
  }

  const startTrip = async ({ dest_kind, dest_id, destLabel, destLat, destLon }) => {
    if (!navigator.geolocation) {
      alert('Geolocation is not available on this device/browser.');
      return;
    }
    if (!navigator.onLine) {
      alert('Offline: Travel tracking requires connection.');
      return;
    }

    const existing = tripRef.current;
    if (
      existing?.active &&
      existing.trip_id &&
      String(existing.dest_kind) === String(dest_kind) &&
      Number(existing.dest_id) === Number(dest_id)
    ) {
      return;
    }

    if (tripRef.current?.active) {
      await stopTripInternal('restarted');
    }

    let trip_id = null;
    try {
      const res = await API.post('/travel/start', {
        dest_kind,
        dest_id,
        lat: location?.lat ?? null,
        lon: location?.lon ?? null,
        client_time: new Date().toISOString().slice(0, 19).replace('T', ' '),
      });
      trip_id = res?.data?.trip_id;
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to start travel tracking.');
      return;
    }

    setTrip({
      active: true,
      trip_id,
      dest_kind,
      dest_id,
      destLabel: destLabel || '',
      destLat: safeNum(destLat),
      destLon: safeNum(destLon),
      startedAt: nowIso(),
      lastPingAt: null,
      lastError: null,
      paused: false,
      destAddressLine: null,
      destSource: null,
    });
    await requestWakeLock();

    lastSentAtRef.current = 0;
    arrivedPromptAtRef.current = 0;
    if (lastSentPosRef) lastSentPosRef.current = { lat: null, lon: null };

    try {
      const watchId = navigator.geolocation.watchPosition(
        async (pos) => {
          const t = tripRef.current;
          if (!t?.active || t.paused) return;

          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          const accuracy = pos.coords.accuracy;
          const speed = pos.coords.speed;
          const heading = pos.coords.heading;

          setLocation({ lat, lon });

          const now = Date.now();
          if (t.destLat != null && t.destLon != null) {
            const distToDest = haversineMeters(lat, lon, t.destLat, t.destLon);
            if (distToDest <= 150) {
              if (now - (arrivedPromptAtRef.current || 0) > 45000) {
                arrivedPromptAtRef.current = now;
                const ok = window.confirm(
                  `Arrived near destination (${t.destLabel || 'destination'}). Stop travel tracking?`
                );
                if (ok) {
                  await stopTripInternal('arrived');
                  return;
                }
              }
            }
          }

          const lastSent = lastSentAtRef.current || 0;
          const speedMps = typeof speed === 'number' ? speed : null;
          const moving = speedMps != null ? speedMps > 1.2 : true;

          const minInterval = moving ? 5000 : 20000;
          const minDistanceM = moving ? 25 : 80;

          const lastPos = lastSentPosRef?.current || {};
          const distSinceLast =
            lastPos.lat != null && lastPos.lon != null
              ? haversineMeters(lat, lon, lastPos.lat, lastPos.lon)
              : Infinity;

          if (now - lastSent < minInterval && distSinceLast < minDistanceM) return;

          lastSentAtRef.current = now;
          if (lastSentPosRef) lastSentPosRef.current = { lat, lon };

          try {
            await API.post('/travel/ping', {
              trip_id: t.trip_id,
              dest_kind: t.dest_kind,
              dest_id: t.dest_id,
              lat,
              lon,
              accuracy_m: accuracy ?? null,
              speed_mps: speed ?? null,
              heading_deg: heading ?? null,
              client_time: new Date().toISOString().slice(0, 19).replace('T', ' '),
            });

            setTrip((p) => ({
              ...(p || {}),
              lastPingAt: serverTimeStr ? String(serverTimeStr) : nowIso(),
              lastError: null,
            }));
          } catch {
            setTrip((p) => ({
              ...(p || {}),
              lastError: 'Ping failed',
            }));
          }
        },
        (err) => {
          setTrip((p) => ({ ...(p || {}), lastError: err?.message || 'Geolocation error' }));
        },
        { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 }
      );

      tripWatchIdRef.current = watchId;

      try {
        if (tripForceTimerRef?.current) clearInterval(tripForceTimerRef.current);
      } catch {}
      if (tripForceTimerRef) {
        tripForceTimerRef.current = setInterval(() => {
          const t = tripRef.current;
          if (!t?.active || t.paused) return;
          if (!navigator.geolocation) return;

          navigator.geolocation.getCurrentPosition(
            (p) => {
              setLocation({ lat: p.coords.latitude, lon: p.coords.longitude });
            },
            () => {},
            { enableHighAccuracy: true, maximumAge: 5000, timeout: 8000 }
          );
        }, 10000);
      }
    } catch {
      setTrip((p) => ({ ...(p || {}), lastError: 'Failed to start GPS watch' }));
    }
  };

  const maybeAutoStopTripFor = async (kind, id, reason = 'checkin') => {
    const t = tripRef.current;
    if (!t?.active || !t.trip_id) return;
    if (String(t.dest_kind) !== String(kind)) return;
    if (Number(t.dest_id) !== Number(id)) return;

    await stopTripInternal(reason);
    showTripToast('Travel tracking stopped (check-in).');
  };

  const resolveWorkDestination = async (w) => {
    const mode = String(w?.location_mode || '');

    if (mode === 'org_location') {
      const fallbackLoc = w.org_location_id ? orgLocationsMap?.[Number(w.org_location_id)] : null;
      const lat = w.org_location_latitude ?? fallbackLoc?.latitude;
      const lon = w.org_location_longitude ?? fallbackLoc?.longitude;
      if (lat == null || lon == null) throw new Error('DEST_MISSING');
      return { lat: Number(lat), lon: Number(lon) };
    }

    if (mode === 'teacher_home') {
      const lat = w.teacher_lat;
      const lon = w.teacher_lon;
      if (lat == null || lon == null) throw new Error('DEST_MISSING');
      return { lat: Number(lat), lon: Number(lon) };
    }

    throw new Error('DEST_REMOTE');
  };

  const openGoogleMapsToLatLon = (destLat, destLon) => {
    const ua = navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isMobile = isAndroid || isIOS;
    const popup = !isMobile ? window.open('', '_blank', 'noopener,noreferrer') : null;

    const navigate = (url) => {
      if (popup && !popup.closed) popup.location.href = url;
      else window.location.href = url;
    };

    const origin = location?.lat && location?.lon ? `${location.lat},${location.lon}` : '';
    const dest = `${destLat},${destLon}`;

    const webUrl = origin
      ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(
          dest
        )}&travelmode=driving`
      : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}&travelmode=driving`;

    if (isAndroid) {
      const navUrl = `google.navigation:q=${encodeURIComponent(dest)}&mode=d`;
      const intentUrl = origin
        ? `intent://maps.google.com/maps?daddr=${encodeURIComponent(dest)}&saddr=${encodeURIComponent(
            origin
          )}&directionsmode=driving#Intent;scheme=https;package=com.google.android.apps.maps;end`
        : `intent://maps.google.com/maps?daddr=${encodeURIComponent(
            dest
          )}&directionsmode=driving#Intent;scheme=https;package=com.google.android.apps.maps;end`;
      navigate(navUrl);
      setTimeout(() => navigate(intentUrl), 250);
      setTimeout(() => navigate(webUrl), 900);
      return;
    }

    if (isIOS) {
      const iosAppUrl = origin
        ? `comgooglemaps://?saddr=${encodeURIComponent(origin)}&daddr=${encodeURIComponent(
            dest
          )}&directionsmode=driving`
        : `comgooglemaps://?daddr=${encodeURIComponent(dest)}&directionsmode=driving`;

      const appleUrl = origin
        ? `maps://?saddr=${encodeURIComponent(origin)}&daddr=${encodeURIComponent(dest)}&dirflg=d`
        : `maps://?daddr=${encodeURIComponent(dest)}&dirflg=d`;

      navigate(iosAppUrl);
      setTimeout(() => navigate(appleUrl), 600);
      setTimeout(() => navigate(webUrl), 1300);
      return;
    }

    navigate(webUrl);
  };

  const normalizeOpsWeather = (payload, dateStr, serverTime) => {
    const root = payload?.data || payload || {};
    const cityData = root?.cityData || root?.data?.cityData || null;

    const mapToArray = (obj, dtField = 'dt') => {
      if (!obj || typeof obj !== 'object') return [];
      return Object.entries(obj).map(([k, v]) => ({ ...(v || {}), [dtField]: k }));
    };

    let hourlyList = [];
    if (cityData?.hourly) {
      hourlyList = Array.isArray(cityData.hourly) ? cityData.hourly : mapToArray(cityData.hourly, 'dt');
    } else if (root?.hourly) {
      hourlyList = Array.isArray(root.hourly) ? root.hourly : mapToArray(root.hourly, 'dt');
    } else if (root?.data?.hourly) {
      hourlyList = Array.isArray(root.data.hourly) ? root.data.hourly : mapToArray(root.data.hourly, 'dt');
    }

    let dailyList = [];
    if (cityData?.daily) {
      dailyList = Array.isArray(cityData.daily) ? cityData.daily : mapToArray(cityData.daily, 'date');
    } else if (root?.daily) {
      dailyList = Array.isArray(root.daily) ? root.daily : mapToArray(root.daily, 'date');
    } else if (root?.data?.daily) {
      dailyList = Array.isArray(root.data.daily) ? root.data.daily : mapToArray(root.data.daily, 'date');
    }

    const getStamp = (x) => String(x?.dt || x?.date || x?.time || x?.timestamp || x?.valid || '');

    let d = dailyList.find((x) => getStamp(x).slice(0, 10) === dateStr) || dailyList[0] || null;

    const todaysHourlyRaw = (hourlyList || []).filter((h) => getStamp(h).slice(0, 10) === dateStr);

    if (!d) {
      if (!todaysHourlyRaw.length) return null;

      const temps = todaysHourlyRaw
        .map((h) => pickNumber(h, ['tmp', 'temp', 'temperature', 't', 't2m']))
        .filter((n) => n != null);

      const tminC = temps.length ? Math.min(...temps) : null;
      const tmaxC = temps.length ? Math.max(...temps) : null;

      const iconRaw = (todaysHourlyRaw.find((h) => h?.icon)?.icon || '').toString().trim() || null;

      d = {
        date: dateStr,
        icon: iconRaw,
        tmin: tminC,
        tmax: tmaxC,
      };
    }

    const iconDaily = normalizeIconFilename(d.icon || d.ico || d.weather_icon);

    const tmaxC =
      pickNumber(d, ['tmax', 'tmax_c', 'max', 'tmp_max', 'temp_max', 'hi', 'tHigh']) ??
      pickNumber(d, ['tmp', 'temp', 'temperature', 't']) ??
      null;

    const tminC =
      pickNumber(d, ['tmin', 'tmin_c', 'min', 'tmp_min', 'temp_min', 'lo', 'tLow']) ??
      pickNumber(d, ['tmp', 'temp', 'temperature', 't']) ??
      null;

    const minutesFromStamp = (stamp) => {
      if (!stamp) return null;
      const s = String(stamp);

      let m = s.match(/T(\d{2}):(\d{2})/);
      if (m && m[1] && m[2]) return Number(m[1]) * 60 + Number(m[2]);

      m = s.match(/\s(\d{2}):(\d{2})/);
      if (m && m[1] && m[2]) return Number(m[1]) * 60 + Number(m[2]);

      return null;
    };

    const nowMinutes = minutesFromStamp(serverTime) ?? null;
    const todaysHourly = [...todaysHourlyRaw].sort((a, b) => getStamp(a).localeCompare(getStamp(b)));

    let alertCode = null;
    let alertHour = null;

    for (const h of todaysHourly) {
      const stamp = getStamp(h);
      const mins = minutesFromStamp(stamp);

      if (nowMinutes != null && mins != null && mins <= nowMinutes) {
        continue;
      }

      const code = getAlertCode(h);
      if (code) {
        alertCode = code;
        alertHour = parseHourLabel(stamp);
        break;
      }
    }

    const alertIcon = alertCode ? normalizeIconFilename(`${alertCode}`) : null;

    return {
      date: dateStr,
      iconDaily,
      tmaxC,
      tminC,
      alertCode,
      alertHour,
      alertIcon,
    };
  };

  const fetchOpsWeather = async () => {
    if (!serverDateStr) return;
    if (!navigator.onLine) {
      setOpsWeather(null);
      return;
    }

    setOpsWeatherLoading(true);
    try {
      const res = await API.get('/ops-weather/san-diego');
      if (res?.data?.empty) {
        setOpsWeather(null);
        return;
      }
      const normalized = normalizeOpsWeather(res.data, serverDateStr, serverTimeStr);
      setOpsWeather(normalized || null);
    } catch {
      setOpsWeather(null);
    } finally {
      setOpsWeatherLoading(false);
    }
  };

  useEffect(() => {
    if (!serverDateStr) return;
    fetchOpsWeather();

    const t = setInterval(() => {
      if (navigator.onLine) fetchOpsWeather();
    }, 10 * 60 * 1000);

    return () => clearInterval(t);
  }, [serverDateStr]); // eslint-disable-line react-hooks/exhaustive-deps

  const prefetchDomainNotesForVisits = async (list) => {
    if (!navigator.onLine) return;
    if (!offlineQueueEnabled) return;

    const slice = (list || []).slice(0, 15);
    for (const v of slice) {
      if (isWorkItem(v)) continue;
      try {
        const res = await API.get(`/visits/${v.id}/domain-notes`);
        const domains = res.data?.domains || [];
        await cacheSet(`cache:visit_domain_notes:${v.id}`, { domains });
      } catch {}
    }
  };

  const normalizeTeacherWorkToUnified = (rows, dateStr) => {
    const list = Array.isArray(rows) ? rows : [];
    return list.map((tw) => {
      const from = tw.from_hhmm || normalizeHHMM(tw.scheduled_at) || '';
      const to = tw.to_hhmm || tw.end_hhmm || '';
      const scheduled_at = tw.scheduled_at || `${dateStr}T${from}:00`;

      return {
        ...tw,
        __kind: 'work',
        _is_work: true,
        scheduled_at,
        scheduled_time: from,
        event_end_time: to ? `${String(to).slice(0, 5)}:00` : null,
        canceled_reason: tw.canceled_reason || tw.cancel_reason || tw.cancelled_reason || null,
        checkin_time_local:
          tw.checkin_hhmm || (tw.checkin_time ? String(tw.checkin_time).slice(11, 16) : null),
        checkout_time_local:
          tw.checkout_hhmm || (tw.checkout_time ? String(tw.checkout_time).slice(11, 16) : null),
      };
    });
  };

  const fetchTeacherWork = async (dateStr) => {
    if (!currentTeacherId) return [];

    if (!navigator.onLine) {
      if (!offlineQueueEnabled) return [];
      const cached = await cacheGet(`cache:teacher_work:${dateStr}:${currentTeacherId}`);
      const data = cached?.data || [];
      return normalizeTeacherWorkToUnified(data, dateStr);
    }

    const res = await API.get('/teacher-work', {
      params: { date: dateStr, teacher_ids: String(currentTeacherId) },
    });

    const raw = res.data || [];
    await cacheSet(`cache:teacher_work:${dateStr}:${currentTeacherId}`, { data: raw });
    return normalizeTeacherWorkToUnified(raw, dateStr);
  };

  const fetchVisits = async (dateStr) => {
    setLoading(true);
    try {
      if (!navigator.onLine) {
        if (!offlineQueueEnabled) {
          return;
        }

        const cachedVisits = await cacheGet(`cache:visits:${dateStr}`);
        const cachedWork = await cacheGet(`cache:teacher_work:${dateStr}:${currentTeacherId}`);

        const vdata = cachedVisits?.data || [];
        const wraw = cachedWork?.data || [];
        const wdata = normalizeTeacherWorkToUnified(wraw, dateStr);

        const merged = [...vdata, ...wdata].sort(sortUnified);

        setVisits(merged.filter((x) => !isWorkItem(x)));
        setTeacherWork(merged.filter((x) => isWorkItem(x)));

        await prefetchDomainNotesForVisits(merged);
        return;
      }

      const params = { date_from: dateStr, date_to: dateStr };
      const res = await API.get('/visits', { params });
      const data = res.data || [];
      data.sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));

      const workData = await fetchTeacherWork(dateStr);
      const merged = [...data, ...workData].sort(sortUnified);

      setVisits(merged.filter((x) => !isWorkItem(x)));
      setTeacherWork(merged.filter((x) => isWorkItem(x)));

      await cacheSet(`cache:visits:${dateStr}`, { data });
      await prefetchDomainNotesForVisits(merged);
    } catch (err) {
      console.error('Failed to load visits/work', err);
      if (offlineQueueEnabled) {
        const cachedVisits = await cacheGet(`cache:visits:${dateStr}`);
        const cachedWork = await cacheGet(`cache:teacher_work:${dateStr}:${currentTeacherId}`);

        const vdata = cachedVisits?.data || [];
        const wraw = cachedWork?.data || [];
        const wdata = normalizeTeacherWorkToUnified(wraw, dateStr);

        const merged = [...vdata, ...wdata].sort(sortUnified);

        setVisits(merged.filter((x) => !isWorkItem(x)));
        setTeacherWork(merged.filter((x) => isWorkItem(x)));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!serverDateStr) return;
    fetchVisits(serverDateStr);
  }, [serverDateStr, offlineQueueEnabled, currentTeacherId]); // eslint-disable-line react-hooks/exhaustive-deps

  const unifiedItems = useMemo(() => {
    const merged = [...(visits || []), ...(teacherWork || [])];
    merged.sort(sortUnified);
    return merged;
  }, [visits, teacherWork]);

  const openStudentModal = async (studentId) => {
    try {
      if (!navigator.onLine) {
        alert('Offline: student details are not available.');
        return;
      }
      const res = await API.get(`/students/${studentId}/full`);
      setStudentDetailsModal({ open: true, student: res.data });
    } catch {
      alert('Failed to load student details');
    }
  };

  const openMapModalFunc = async (visit) => {
    try {
      if (!navigator.onLine) {
        alert('Offline: map data is not available.');
        return;
      }

      const res = await API.get('/visit-map-locations', {
        params: { student_id: visit.student_id, teacher_id: visit.teacher_id },
      });

      let studentObj = res.data.student;
      const teacherObj = res.data.teacher;

      const isClinic = String(visit.location_mode || 'at_home') === 'at_clinic';
      if (isClinic && visit.org_location_id) {
        const loc = orgLocationsMap?.[Number(visit.org_location_id)];
        const clinicDest = buildClinicDestFromLocationRow(loc);
        if (clinicDest?.lat != null && clinicDest?.lon != null) {
          studentObj = clinicDest;
        } else {
          alert('Clinic location is missing coordinates (latitude/longitude).');
        }
      }

      setMapModal({ open: true, student: studentObj, teacher: teacherObj });
    } catch {
      alert('Failed to fetch map data');
    }
  };

  const openWorkNotesModal = (w) => {
    const teacherName = String(currentUser?.name || 'You').trim();
    const workType = String(w?.work_type || 'other');
    setWorkNotesModal({
      open: true,
      teacherName,
      workType,
      workTitle: String(w?.title || '').trim(),
      notes: String(w?.notes || '').trim(),
      scheduledAt: String(w?.scheduled_at || ''),
    });
  };

  const openWorkLocationModal = async (tw) => {
    if (!tw) return;
    if (!navigator.onLine) {
      alert('Offline: map data is not available.');
      return;
    }

    const workType = String(tw.work_type || 'other');
    let locationName = '';
    let addressLine = '';
    let lat = null;
    let lon = null;

    if (tw.location_mode === 'org_location') {
      const fallbackLoc = tw.org_location_id ? orgLocationsMap?.[Number(tw.org_location_id)] : null;

      locationName =
        String(tw.org_location_name || '').trim() ||
        String(fallbackLoc?.name || '').trim() ||
        'Office';

      const parts = [
        tw.org_location_street ?? fallbackLoc?.street,
        tw.org_location_city ?? fallbackLoc?.city,
        tw.org_location_state ?? fallbackLoc?.state,
        tw.org_location_zip ?? fallbackLoc?.zip,
        tw.org_location_country ?? fallbackLoc?.country,
      ]
        .map((x) => String(x || '').trim())
        .filter(Boolean);
      addressLine = parts.join(', ');

      const latSrc = tw.org_location_latitude ?? fallbackLoc?.latitude;
      const lonSrc = tw.org_location_longitude ?? fallbackLoc?.longitude;

      lat = latSrc != null ? Number(latSrc) : null;
      lon = lonSrc != null ? Number(lonSrc) : null;
    } else if (tw.location_mode === 'teacher_home') {
      locationName = 'Teacher home';
      const parts = [tw.teacher_street, tw.teacher_city, tw.teacher_state, tw.teacher_zip, tw.teacher_country]
        .map((x) => String(x || '').trim())
        .filter(Boolean);
      addressLine = parts.join(', ');
      lat = tw.teacher_lat != null ? Number(tw.teacher_lat) : null;
      lon = tw.teacher_lon != null ? Number(tw.teacher_lon) : null;
    } else if (tw.location_mode === 'remote') {
      locationName = 'Remote';
      addressLine = 'Remote work';
      lat = null;
      lon = null;
    } else {
      locationName = tw.org_location_name || 'Office';
    }

    const title = locationName ? `Work: ${workType} - ${locationName}` : `Work: ${workType}`;

    const dest = {
      name: locationName,
      address: addressLine,
      lat,
      lon,
      latitude: lat,
      longitude: lon,
    };

    const teacherObj = {
      name: currentUser?.name || 'You',
      lat: location.lat,
      lon: location.lon,
      latitude: location.lat,
      longitude: location.lon,
    };

    setWorkLocModal({
      open: true,
      title,
      addressLine,
      dest,
      teacher: teacherObj,
    });
  };

  const openNotesHistoryModal = async (studentId, studentName) => {
    setNotesHistoryLoading(true);
    setShowNotesHistory(true);
    setNotesHistoryStudent(studentName);

    try {
      if (!navigator.onLine) {
        alert('Offline: notes history is not available.');
        setNotesHistory([]);
        return;
      }
      const res = await API.get(`/students/${studentId}/notes-history`);
      setNotesHistory(res.data || []);
    } catch {
      setNotesHistory([]);
    } finally {
      setNotesHistoryLoading(false);
    }
  };

  const handleCancelVisit = async (reason) => {
    if (!reason || !cancelVisitId) return;

    if (!navigator.onLine) {
      if (!offlineQueueEnabled) {
        alert('Offline: cancel is not available. Please reconnect and try again.');
        return;
      }
      setCancelLoading(true);
      try {
        await enqueueOutbox({
          kind: 'cancel',
          visitId: cancelVisitId,
          method: 'PATCH',
          url: `/visits/${cancelVisitId}/cancel`,
          data: { reason },
          dedupeKey: `cancel:${cancelVisitId}`,
        });
        await refreshOutboxStats();

        setVisits((prev) =>
          prev.map((v) =>
            v.id === cancelVisitId
              ? {
                  ...v,
                  status: 'canceled',
                  notes: reason,
                }
              : v
          )
        );

        setCancelVisitId(null);
      } catch {
        alert('Failed to queue cancel.');
      } finally {
        setCancelLoading(false);
      }
      return;
    }

    setCancelLoading(true);
    try {
      await API.patch(`/visits/${cancelVisitId}/cancel`, { reason });
      setCancelVisitId(null);
      fetchVisits(serverDateStr);
    } catch {
      alert('Failed to cancel visit!');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCancelWork = async (reason) => {
    if (!reason || !cancelWorkId) return;

    if (!navigator.onLine) {
      if (!offlineQueueEnabled) {
        alert('Offline: cancel is not available. Please reconnect and try again.');
        return;
      }
      setCancelWorkLoading(true);
      try {
        await enqueueOutbox({
          kind: 'work-cancel',
          workId: cancelWorkId,
          method: 'POST',
          url: `/teacher-work/${cancelWorkId}/cancel`,
          data: { reason },
          dedupeKey: `work-cancel:${cancelWorkId}`,
        });
        await refreshOutboxStats();

        setTeacherWork((prev) =>
          prev.map((w) =>
            w.id === cancelWorkId
              ? {
                  ...w,
                  status: 'canceled',
                  canceled_reason: reason,
                  notes: w.notes,
                }
              : w
          )
        );

        setCancelWorkId(null);
      } catch {
        alert('Failed to queue work cancel.');
      } finally {
        setCancelWorkLoading(false);
      }
      return;
    }

    setCancelWorkLoading(true);
    try {
      await API.post(`/teacher-work/${cancelWorkId}/cancel`, { reason });
      setCancelWorkId(null);
      fetchVisits(serverDateStr);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to cancel work!');
    } finally {
      setCancelWorkLoading(false);
    }
  };

  const handleCheckin = async (id) => {
    if (!location.lat) return alert('Geolocation required');

    if (!navigator.onLine) {
      if (!offlineQueueEnabled) {
        alert('Offline: check-in is not available. Please reconnect and try again.');
        return;
      }
      try {
        await enqueueOutbox({
          kind: 'checkin',
          visitId: id,
          method: 'PATCH',
          url: `/visits/${id}/checkin`,
          data: location,
          dedupeKey: `checkin:${id}`,
        });
        await refreshOutboxStats();

        const t = nowIso();
        setVisits((prev) =>
          prev.map((v) =>
            v.id === id
              ? {
                  ...v,
                  checkin_time: v.checkin_time || t,
                  checkin_time_local: v.checkin_time_local || 'Pending sync',
                }
              : v
          )
        );

        await maybeAutoStopTripFor('visit', id, 'visit_checkin');
      } catch {
        alert('Failed to queue check-in.');
      }
      return;
    }

    try {
      await API.patch(`/visits/${id}/checkin`, location);
      await maybeAutoStopTripFor('visit', id, 'visit_checkin');
      fetchVisits(serverDateStr);
    } catch {
      alert('Failed to check-in');
    }
  };

  const handleWorkCheckin = async (id) => {
    if (!location.lat) return alert('Geolocation required');

    if (!navigator.onLine) {
      if (!offlineQueueEnabled) {
        alert('Offline: check-in is not available. Please reconnect and try again.');
        return;
      }
      try {
        await enqueueOutbox({
          kind: 'work-checkin',
          workId: id,
          method: 'PATCH',
          url: `/teacher-work/${id}/checkin`,
          data: location,
          dedupeKey: `work-checkin:${id}`,
        });
        await refreshOutboxStats();

        const t = nowIso();
        setTeacherWork((prev) =>
          prev.map((w) =>
            w.id === id
              ? {
                  ...w,
                  checkin_time: w.checkin_time || t,
                  checkin_time_local: w.checkin_time_local || 'Pending sync',
                }
              : w
          )
        );

        await maybeAutoStopTripFor('work', id, 'work_checkin');
      } catch {
        alert('Failed to queue work check-in.');
      }
      return;
    }

    try {
      await API.patch(`/teacher-work/${id}/checkin`, location);
      await maybeAutoStopTripFor('work', id, 'work_checkin');
      fetchVisits(serverDateStr);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to check-in work');
    }
  };

  const handleWorkCheckout = async (id) => {
    if (!location.lat) return alert('Geolocation required');

    if (!navigator.onLine) {
      if (!offlineQueueEnabled) {
        alert('Offline: checkout is not available. Please reconnect and try again.');
        return;
      }
      try {
        await enqueueOutbox({
          kind: 'work-checkout',
          workId: id,
          method: 'PATCH',
          url: `/teacher-work/${id}/checkout`,
          data: location,
          dedupeKey: `work-checkout:${id}`,
        });
        await refreshOutboxStats();

        const t = nowIso();
        setTeacherWork((prev) =>
          prev.map((w) =>
            w.id === id
              ? {
                  ...w,
                  checkout_time: w.checkout_time || t,
                  checkout_time_local: w.checkout_time_local || 'Pending sync',
                  status: w.status === 'canceled' ? w.status : 'complete',
                }
              : w
          )
        );
      } catch {
        alert('Failed to queue work checkout.');
      }
      return;
    }

    try {
      await API.patch(`/teacher-work/${id}/checkout`, location);
      fetchVisits(serverDateStr);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to checkout work');
    }
  };

  const openNotesModal = async (visit, mode) => {
    if (!navigator.onLine && !offlineQueueEnabled) {
      alert('Offline: notes are not available. Please reconnect.');
      return;
    }

    setNotesModalVisit(visit);
    setNotesEditMode(mode === 'edit');
    setShowNotesModal(true);
    setNotesModalLoading(true);
    setDomainList([]);
    setDomainNotes({});
    resetAiSuggestionState();

    const cacheKey = `cache:visit_domain_notes:${visit.id}`;

    try {
      if (!navigator.onLine) {
        const cached = await cacheGet(cacheKey);
        const domains = cached?.domains || [];
        if (!domains.length) {
          setNotesModalLoading(false);
          alert('Offline: domain list is not cached yet. Please go online once to load domains for this visit.');
          return;
        }

        setDomainList(domains);
        const initial = {};
        domains.forEach((d) => {
          initial[d.id] = d.note || '';
        });
        setDomainNotes(initial);
        setNotesModalLoading(false);
        return;
      }

      const res = await API.get(`/visits/${visit.id}/domain-notes`);
      const domains = res.data?.domains || [];
      setDomainList(domains);

      const initial = {};
      domains.forEach((d) => {
        initial[d.id] = d.note || '';
      });
      setDomainNotes(initial);

      await cacheSet(cacheKey, { domains });
    } catch (err) {
      console.error('Failed to load domain notes', err);

      const cached = await cacheGet(cacheKey);
      const domains = cached?.domains || [];
      if (domains.length) {
        setDomainList(domains);
        const initial = {};
        domains.forEach((d) => {
          initial[d.id] = d.note || '';
        });
        setDomainNotes(initial);
      } else {
        alert('Failed to load domain notes for this visit.');
      }
    } finally {
      setNotesModalLoading(false);
    }
  };

  const handleCheckout = (visit) => {
    if (!navigator.onLine && !offlineQueueEnabled) {
      alert('Offline: checkout is not available. Please reconnect.');
      return;
    }
    openNotesModal(visit, 'finalize');
  };

  const generateAiVisitSuggestion = async () => {
    if (!notesModalVisit?.id) return;
    if (aiNoteGeneratedOnce) return;

    if (aiAccess.loading) {
      setAiNoteError('Checking AI access settings...');
      return;
    }

    if (!canUseAiNotes) {
      setAiNoteError(getAiDisabledMessage('visit_note'));
      return;
    }

    if (!navigator.onLine) {
      setAiNoteError('AI suggestion requires an online connection.');
      return;
    }

    setAiNoteLoading(true);
    setAiNoteError('');
    setAiApplyMessage('');
    setAiCopiedField('');

    try {
      const draft_domain_notes = Object.entries(domainNotes || {}).map(([domain_id, note]) => ({
        domain_id: Number(domain_id),
        note: String(note || ''),
      }));

      const res = await API.post(`/ai/visit-note-generate/${notesModalVisit.id}`, {
        draft_domain_notes,
      });

      const result = res?.data?.result || null;
      if (!result) {
        throw new Error('AI did not return a suggestion.');
      }

      setAiNoteResult(result);
      setAiNoteGeneratedOnce(true);
    } catch (err) {
      console.error('AI visit suggestion error:', err);
      setAiNoteError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to generate AI suggestion.'
      );
    } finally {
      setAiNoteLoading(false);
    }
  };

  const handleCopyAiField = async (kind) => {
    if (!aiNoteResult) return;

    const value =
      kind === 'short'
        ? aiNoteResult.short_note
        : kind === 'detailed'
        ? aiNoteResult.detailed_note
        : '';

    const ok = await copyToClipboardSafe(value);
    if (!ok) {
      alert('Failed to copy text.');
      return;
    }

    setAiCopiedField(kind);
    setTimeout(() => {
      setAiCopiedField((prev) => (prev === kind ? '' : prev));
    }, 1800);
  };

  const applyAiHighlightsToDomainNotes = () => {
    if (!aiNoteResult || !Array.isArray(aiNoteResult.domain_highlights)) return;
    if (!Array.isArray(domainList) || !domainList.length) return;

    const next = { ...(domainNotes || {}) };
    let applied = 0;

    aiNoteResult.domain_highlights.forEach((highlight) => {
      const matchedDomain = findBestDomainMatch(highlight, domainList);
      const summary = String(highlight?.summary || '').trim();
      if (!matchedDomain || !summary) return;

      const domainId = matchedDomain.id;
      const merged = mergeNoteText(next[domainId], summary);

      if (merged !== (next[domainId] || '')) {
        next[domainId] = merged;
        applied += 1;
      }
    });

    if (applied > 0) {
      setDomainNotes(next);
      setAiApplyMessage(
        `Applied ${applied} AI domain highlight${applied === 1 ? '' : 's'} into the domain notes below.`
      );
      setTimeout(() => setAiApplyMessage(''), 3000);
    } else {
      setAiApplyMessage('No matching domain boxes were found for the returned AI highlights.');
      setTimeout(() => setAiApplyMessage(''), 3000);
    }
  };

  const handleSaveNotes = async () => {
    if (!notesModalVisit) return;

    const notesPayload = Object.entries(domainNotes || {}).map(([domainId, note]) => ({
      domain_id: Number(domainId),
      note: note || '',
    }));

    const visitId = notesModalVisit.id;

    const cacheKey = `cache:visit_domain_notes:${visitId}`;
    try {
      const cached = (await cacheGet(cacheKey)) || {};
      const domains = domainList.map((d) => ({
        ...d,
        note: domainNotes?.[d.id] ?? d.note ?? '',
      }));
      await cacheSet(cacheKey, { ...(cached || {}), domains });
    } catch {}

    if (!navigator.onLine) {
      if (!offlineQueueEnabled) {
        alert('Offline: saving is disabled. Please reconnect and try again.');
        return;
      }
      if (!notesEditMode && !location.lat) {
        alert('Geolocation required!');
        return;
      }

      setNotesSaving(true);
      try {
        await enqueueOutbox({
          kind: 'domain-notes',
          visitId,
          method: 'POST',
          url: `/visits/${visitId}/domain-notes`,
          data: { notes: notesPayload },
          dedupeKey: `domain-notes:${visitId}`,
        });

        if (!notesEditMode) {
          await enqueueOutbox({
            kind: 'checkout',
            visitId,
            method: 'PATCH',
            url: `/visits/${visitId}/checkout`,
            data: location,
            dedupeKey: `checkout:${visitId}`,
          });

          const t = nowIso();
          setVisits((prev) =>
            prev.map((v) =>
              v.id === visitId
                ? {
                    ...v,
                    checkout_time: v.checkout_time || t,
                    checkout_time_local: v.checkout_time_local || 'Pending sync',
                    status: v.status === 'canceled' ? v.status : 'complete',
                  }
                : v
            )
          );
        }

        await refreshOutboxStats();

        setShowNotesModal(false);
        setNotesModalVisit(null);
        resetAiSuggestionState();
      } catch (err) {
        console.error(err);
        alert('Failed to queue notes/checkout.');
      } finally {
        setNotesSaving(false);
      }
      return;
    }

    setNotesSaving(true);
    try {
      await API.post(`/visits/${visitId}/domain-notes`, { notes: notesPayload });

      if (!notesEditMode) {
        if (!location.lat) {
          alert('Geolocation required!');
          setNotesSaving(false);
          return;
        }
        await API.patch(`/visits/${visitId}/checkout`, location);
      }

      setShowNotesModal(false);
      setNotesModalVisit(null);
      resetAiSuggestionState();
      fetchVisits(serverDateStr);
    } catch (err) {
      console.error(err);
      alert('Failed to save notes!');
    } finally {
      setNotesSaving(false);
    }
  };

  useEffect(() => {
    let interval;
    if (qrModalVisitId && !visitConfirmed) {
      interval = setInterval(async () => {
        try {
          if (!navigator.onLine) return;
          const res = await API.get(`/visits/${qrModalVisitId}`);
          if (res.data && res.data.g_sign_confirmed) {
            setVisitConfirmed(true);
            fetchVisits(serverDateStr);
            clearInterval(interval);
          }
        } catch {}
      }, 2000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [qrModalVisitId, visitConfirmed, serverDateStr]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseQRModal = () => {
    setQrModalVisitId(null);
    setVisitConfirmed(false);
    fetchVisits(serverDateStr);
  };

  const canCancel = (v) =>
    (v.reassigned_status === 'none' || v.reassigned_status === 'pending') &&
    v.status === 'draft' &&
    !v.checkin_time;

  const canCancelWork = (w, serverDateStr) => {
    const d = String(w?.scheduled_at || '').slice(0, 10);
    if (!d || !serverDateStr) return false;
    if (d < serverDateStr) return false;
    if (w.status !== 'draft') return false;
    if (w.checkin_time) return false;
    return true;
  };

  const openReminderModal = async (studentId, studentName) => {
    if (!studentId) return;

    setReminderModal({ open: true, student_id: studentId, student_name: studentName || '' });

    const cachedState = reminderCache?.[studentId];
    if (cachedState) {
      setReminderValue(cachedState?.reminder_text || '');
      setReminderMeta(cachedState || null);
    } else if (offlineQueueEnabled) {
      const cachedLocal = await cacheGet(`cache:student_reminder:${studentId}`);
      if (cachedLocal) {
        setReminderCache((prev) => ({ ...prev, [studentId]: cachedLocal }));
        setReminderValue(cachedLocal?.reminder_text || '');
        setReminderMeta(cachedLocal || null);
      } else {
        setReminderValue('');
        setReminderMeta(null);
      }
    } else {
      setReminderValue('');
      setReminderMeta(null);
    }

    if (!navigator.onLine) return;

    setReminderLoading(true);
    try {
      const res = await API.get(`/students/${studentId}/reminder`);
      const payload = res?.data || {};
      const normalized = {
        reminder_text: payload.reminder_text || '',
        updated_at: payload.updated_at || null,
        updated_by_name: payload.updated_by_name || null,
      };

      setReminderCache((prev) => ({ ...prev, [studentId]: normalized }));
      setReminderValue(normalized.reminder_text || '');
      setReminderMeta(normalized);
      await cacheSet(`cache:student_reminder:${studentId}`, normalized);
    } catch (err) {
      const code = err?.response?.status;
      if (code !== 404) {
        console.error(err);
        alert(err?.response?.data?.error || 'Failed to load student reminder.');
      }
    } finally {
      setReminderLoading(false);
    }
  };

  const closeReminderModal = () => {
    setReminderModal({ open: false, student_id: null, student_name: '' });
    setReminderValue('');
    setReminderMeta(null);
    setReminderLoading(false);
    setReminderSaving(false);
  };

  const saveReminder = async () => {
    const studentId = reminderModal?.student_id;
    if (!studentId) return;

    setReminderSaving(true);
    try {
      if (!navigator.onLine) {
        if (!offlineQueueEnabled) {
          alert('Offline: saving reminder is disabled. Please reconnect.');
          return;
        }

        const normalized = {
          reminder_text: reminderValue || '',
          updated_at: nowIso(),
          updated_by_name: currentUser?.name || null,
        };

        await enqueueOutbox({
          kind: 'student-reminder',
          method: 'PUT',
          url: `/students/${studentId}/reminder`,
          data: { reminder_text: reminderValue || '' },
          dedupeKey: `student-reminder:${studentId}`,
        });

        setReminderCache((prev) => ({ ...prev, [studentId]: normalized }));
        setReminderMeta(normalized);
        await cacheSet(`cache:student_reminder:${studentId}`, normalized);

        await refreshOutboxStats();
        closeReminderModal();
        return;
      }

      await API.put(`/students/${studentId}/reminder`, { reminder_text: reminderValue || '' });

      try {
        const res = await API.get(`/students/${studentId}/reminder`);
        const payload = res?.data || {};
        const normalized = {
          reminder_text: payload.reminder_text || '',
          updated_at: payload.updated_at || null,
          updated_by_name: payload.updated_by_name || null,
        };
        setReminderCache((prev) => ({ ...prev, [studentId]: normalized }));
        await cacheSet(`cache:student_reminder:${studentId}`, normalized);
      } catch {
        const normalized = {
          reminder_text: reminderValue || '',
          updated_at: nowIso(),
          updated_by_name: currentUser?.name || null,
        };
        setReminderCache((prev) => ({ ...prev, [studentId]: normalized }));
        await cacheSet(`cache:student_reminder:${studentId}`, normalized);
      }

      closeReminderModal();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.error || 'Failed to save reminder.');
    } finally {
      setReminderSaving(false);
    }
  };

  const queueMentorFeedback = async (visitId, payload) => {
    if (!offlineQueueEnabled) throw new Error('OFFLINE_QUEUE_DISABLED');
    await enqueueOutbox({
      kind: 'mentor-feedback',
      visitId,
      method: 'POST',
      url: `/training/feedback/${visitId}`,
      data: payload,
      dedupeKey: `mentor-feedback:${visitId}`,
    });
    await refreshOutboxStats();
  };

  const getVisitDestination = async (visit) => {
    if (!navigator.onLine) throw new Error('OFFLINE');

    const student_id = visit?.student_id;
    const teacher_id = visit?.teacher_id;
    if (!student_id || !teacher_id) throw new Error('MISSING_IDS');

    const res = await API.get('/visit-map-locations', { params: { student_id, teacher_id } });
    const s = res.data?.student || {};

    let destLat = s.lat ?? s.latitude ?? s.primary_lat;
    let destLon = s.lon ?? s.longitude ?? s.primary_lon;

    const isClinic = String(visit?.location_mode || 'at_home') === 'at_clinic';
    if (isClinic && visit?.org_location_id) {
      const loc = orgLocationsMap?.[Number(visit.org_location_id)];
      const clinicDest = buildClinicDestFromLocationRow(loc);
      if (clinicDest?.lat != null && clinicDest?.lon != null) {
        destLat = clinicDest.lat;
        destLon = clinicDest.lon;
      } else {
        throw new Error('CLINIC_MISSING');
      }
    }

    if (destLat == null || destLon == null) throw new Error('DEST_MISSING');

    const studentName =
      `${visit?.student_last_name || ''}${visit?.student_last_name ? ', ' : ''}${visit?.student_first_name || ''}`.trim();
    const label = `Visit: ${studentName || 'Student'}`;

    return { lat: Number(destLat), lon: Number(destLon), label };
  };

  const openGoogleMapsDirections = async (student_id, teacher_id, visit) => {
    const ua = navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isMobile = isAndroid || isIOS;
    const popup = !isMobile ? window.open('', '_blank', 'noopener,noreferrer') : null;

    const navigate = (url) => {
      if (popup && !popup.closed) popup.location.href = url;
      else window.location.href = url;
    };

    try {
      if (!navigator.onLine) {
        alert('Offline: cannot load destination location.');
        if (popup && !popup.closed) popup.close();
        return;
      }

      const res = await API.get('/visit-map-locations', { params: { student_id, teacher_id } });
      const s = res.data?.student || {};

      let destLat = s.lat ?? s.latitude ?? s.primary_lat;
      let destLon = s.lon ?? s.longitude ?? s.primary_lon;

      const isClinic = String(visit?.location_mode || 'at_home') === 'at_clinic';
      if (isClinic && visit?.org_location_id) {
        const loc = orgLocationsMap?.[Number(visit.org_location_id)];
        const clinicDest = buildClinicDestFromLocationRow(loc);

        if (clinicDest?.lat != null && clinicDest?.lon != null) {
          destLat = clinicDest.lat;
          destLon = clinicDest.lon;
        } else {
          alert('Clinic location is missing coordinates (latitude/longitude).');
          if (popup && !popup.closed) popup.close();
          return;
        }
      }

      if (destLat == null || destLon == null) {
        alert('Destination location is missing.');
        if (popup && !popup.closed) popup.close();
        return;
      }

      const origin = location && location.lat && location.lon ? `${location.lat},${location.lon}` : '';
      const dest = `${destLat},${destLon}`;

      const webUrl = origin
        ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(
            dest
          )}&travelmode=driving`
        : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}&travelmode=driving`;

      if (isAndroid) {
        const navUrl = `google.navigation:q=${encodeURIComponent(dest)}&mode=d`;
        const intentUrl = origin
          ? `intent://maps.google.com/maps?daddr=${encodeURIComponent(dest)}&saddr=${encodeURIComponent(
              origin
            )}&directionsmode=driving#Intent;scheme=https;package=com.google.android.apps.maps;end`
          : `intent://maps.google.com/maps?daddr=${encodeURIComponent(
              dest
            )}&directionsmode=driving#Intent;scheme=https;package=com.google.android.apps.maps;end`;
        navigate(navUrl);
        setTimeout(() => navigate(intentUrl), 250);
        setTimeout(() => navigate(webUrl), 900);
        return;
      }

      if (isIOS) {
        const iosAppUrl = origin
          ? `comgooglemaps://?saddr=${encodeURIComponent(origin)}&daddr=${encodeURIComponent(
              dest
            )}&directionsmode=driving`
          : `comgooglemaps://?daddr=${encodeURIComponent(dest)}&directionsmode=driving`;

        const appleUrl = origin
          ? `maps://?saddr=${encodeURIComponent(origin)}&daddr=${encodeURIComponent(dest)}&dirflg=d`
          : `maps://?daddr=${encodeURIComponent(dest)}&dirflg=d`;

        navigate(iosAppUrl);
        setTimeout(() => navigate(appleUrl), 600);
        setTimeout(() => navigate(webUrl), 1300);
        return;
      }

      navigate(webUrl);
    } catch (e) {
      console.error(e);
      alert('Failed to open directions.');
      if (popup && !popup.closed) popup.close();
    }
  };

  const grouped = useMemo(() => groupByDate(unifiedItems), [unifiedItems]);

  const ConnectionBar = () => {
    if (!offlineQueueEnabled) return null;

    const pending = outboxStats.pending || 0;
    const failed = outboxStats.failed || 0;
    const headErr = outboxStats.headError;

    if (!isOnline) {
      return (
        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 flex items-start justify-between gap-3">
          <div className="text-sm text-amber-900">
            <div className="font-semibold flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
              Offline mode
            </div>
            <div className="text-xs text-amber-800 mt-0.5">
              Your actions will be saved and synced automatically when you’re back online.
              {pending ? <span className="ml-2 font-semibold">Queued: {pending}</span> : null}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refreshOutboxStats()}
              className="text-xs px-3 py-1 rounded-lg border border-amber-300 text-amber-900 bg-white hover:bg-amber-100"
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }

    if (pending > 0 || syncing || failed > 0) {
      return (
        <div className="mb-3 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 flex items-start justify-between gap-3">
          <div className="text-sm text-sky-900">
            <div className="font-semibold flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-sky-500" />
              Online
              {syncing ? <span className="text-xs font-semibold text-sky-700">(syncing…)</span> : null}
            </div>
            <div className="text-xs text-sky-800 mt-0.5">
              {pending ? (
                <>
                  Pending sync: <span className="font-semibold">{pending}</span>
                  {failed && headErr ? <span className="ml-2 text-red-700 font-semibold">Issue: {headErr}</span> : null}
                </>
              ) : failed && headErr ? (
                <span className="text-red-700 font-semibold">Sync issue: {headErr}</span>
              ) : (
                <span className="text-sky-800">All changes synced.</span>
              )}
            </div>
            {justSyncedMsg ? <div className="text-[11px] text-sky-700 mt-0.5">{justSyncedMsg}</div> : null}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => flushNow({ silent: false })}
              disabled={syncing || pending === 0}
              className={`text-xs px-3 py-1 rounded-lg border ${
                syncing || pending === 0
                  ? 'border-sky-200 text-sky-400 bg-white cursor-not-allowed'
                  : 'border-sky-300 text-sky-900 bg-white hover:bg-sky-100'
              }`}
            >
              Sync now
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 flex items-center justify-between">
        <div className="text-sm text-emerald-900 font-semibold flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
          Online
          <span className="text-xs font-normal text-emerald-700">All set.</span>
        </div>
        {justSyncedMsg ? <div className="text-xs text-emerald-800">{justSyncedMsg}</div> : null}
      </div>
    );
  };

  const WeatherWidget = () => {
    if (!isOnline) return null;
    if (opsWeatherLoading) {
      return (
        <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full border bg-white text-xs text-slate-500">
          <span className="inline-block w-2 h-2 rounded-full bg-slate-300" />
          Weather…
        </div>
      );
    }
    if (!opsWeather) return null;

    const dailySrc = opsWeatherIconSrc(opsWeather.iconDaily);
    const alertSrc = opsWeather.alertIcon ? opsWeatherIconSrc(opsWeather.alertIcon) : null;
    const tmaxColor = getTemperatureColorCelsius(opsWeather.tmaxC);
    const tminColor = getTemperatureColorCelsius(opsWeather.tminC);

    return (
      <button
        type="button"
        onClick={() => window.open(W2U_SD_URL, '_blank', 'noopener,noreferrer')}
        className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full border bg-white hover:bg-slate-50 transition-colors"
        title="Open Weather2Umbrella (San Diego)"
      >
        {dailySrc ? (
          <img src={dailySrc} alt="weather" className="w-6 h-6 object-contain" />
        ) : (
          <span className="text-xs text-slate-500">Weather</span>
        )}

        <div className="flex items-center gap-1 text-xs font-semibold">
          <span style={{ color: tminColor }}>{formatF(opsWeather.tminC)}</span>
          <span className="text-slate-400">/</span>
          <span style={{ color: tmaxColor }}>{formatF(opsWeather.tmaxC)}</span>
        </div>

        {alertSrc ? (
          <div className="flex items-center gap-1 ml-1">
            <img src={alertSrc} alt="alert" className="w-5 h-5 object-contain" />
            {opsWeather.alertHour ? (
              <span className="text-[11px] font-semibold text-slate-700">{opsWeather.alertHour}</span>
            ) : null}
          </div>
        ) : null}
      </button>
    );
  };

  const isDoneStatus = (st) => {
    const s = String(st || '').toLowerCase();
    return s === 'complete' || s === 'canceled';
  };

  const canStartTripForVisit = (v, serverDateStr) => {
    if (!isOnline) return false;
    if (!v) return false;
    if (isDoneStatus(v.status)) return false;

    const d = String(v.scheduled_at || '').slice(0, 10);
    if (serverDateStr && d && d !== serverDateStr) return false;
    return true;
  };

  const canStartTripForWork = (w, serverDateStr) => {
    if (!isOnline) return false;
    if (!w) return false;
    if (isDoneStatus(w.status)) return false;

    const d = String(w.scheduled_at || '').slice(0, 10);
    if (serverDateStr && d && d !== serverDateStr) return false;
    return true;
  };

  function fmtOrgTimeHHMMSS(dtLike, tz) {
    if (!dtLike) return '';
    const s = String(dtLike);

    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(s)) {
      return s.slice(11, 19);
    }

    try {
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return s.slice(11, 19);
      if (!tz) return d.toTimeString().slice(0, 8);

      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).formatToParts(d);

      const hh = parts.find((p) => p.type === 'hour')?.value || '00';
      const mm = parts.find((p) => p.type === 'minute')?.value || '00';
      const ss = parts.find((p) => p.type === 'second')?.value || '00';
      return `${hh}:${mm}:${ss}`;
    } catch {
      return s.slice(11, 19);
    }
  }

  return (
    <div className="w-full px-0 sm:px-1">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold">Today's Visits</h2>
          <WeatherWidget />
        </div>

        <div className="flex items-center gap-2">
          {offlineQueueEnabled && outboxStats.pending ? (
            <span className="text-[11px] px-2 py-1 rounded-full border bg-amber-50 border-amber-200 text-amber-800 font-semibold">
              Pending: {outboxStats.pending}
            </span>
          ) : null}
          <span
            className={`text-[11px] px-2 py-1 rounded-full border font-semibold ${
              isOnline
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'
            }`}
          >
            {isOnline ? 'Online' : 'Offline'}
          </span>
          {!offlineQueueEnabled ? (
            <span className="text-[11px] px-2 py-1 rounded-full border bg-slate-50 border-slate-200 text-slate-700 font-semibold">
              Offline queue: OFF
            </span>
          ) : null}
        </div>
      </div>

      {isOnline && opsWeather && (
        <button
          type="button"
          onClick={() => window.open(W2U_SD_URL, '_blank', 'noopener,noreferrer')}
          className="sm:hidden mb-3 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-slate-50"
          title="Open Weather2Umbrella (San Diego)"
        >
          <div className="flex items-center gap-2">
            {opsWeatherIconSrc(opsWeather.iconDaily) ? (
              <img src={opsWeatherIconSrc(opsWeather.iconDaily)} alt="weather" className="w-7 h-7 object-contain" />
            ) : null}
            <div className="flex flex-col items-start">
              <div className="text-xs font-semibold text-slate-800">San Diego</div>
              <div className="text-[11px] text-slate-500">Tap to open forecast</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="text-xs font-semibold">
              <span style={{ color: getTemperatureColorCelsius(opsWeather.tminC) }}>{formatF(opsWeather.tminC)}</span>
              <span className="text-slate-400 mx-1">/</span>
              <span style={{ color: getTemperatureColorCelsius(opsWeather.tmaxC) }}>{formatF(opsWeather.tmaxC)}</span>
            </div>
            {opsWeather.alertIcon && opsWeatherIconSrc(opsWeather.alertIcon) ? (
              <div className="flex items-center gap-1">
                <img src={opsWeatherIconSrc(opsWeather.alertIcon)} alt="alert" className="w-6 h-6 object-contain" />
                {opsWeather.alertHour ? (
                  <span className="text-[11px] font-semibold text-slate-700">{opsWeather.alertHour}</span>
                ) : null}
              </div>
            ) : null}
          </div>
        </button>
      )}

      {!isOnline && !offlineQueueEnabled ? (
        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          You are offline. Offline actions are disabled—please reconnect to check-in/checkout/cancel/save.
        </div>
      ) : null}

      <ConnectionBar />

      {trip?.active ? (
        <div className="mb-3 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 flex items-start justify-between gap-3">
          <div className="text-sm text-indigo-900">
            <div className="font-semibold flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-indigo-600" />
              Travel tracking ON
              {trip.paused ? <span className="text-xs font-semibold text-indigo-700">(paused)</span> : null}
            </div>

            <div className="text-xs text-indigo-800 mt-0.5">
              Destination: <span className="font-semibold">{trip.destLabel || '—'}</span>
              {trip.lastPingAt ? (
                <span className="ml-2 text-indigo-700">
                  Last ping: {fmtOrgTimeHHMMSS(trip.lastPingAt, serverTz)}
                </span>
              ) : null}
              {trip.lastError ? <span className="ml-2 text-red-700 font-semibold">{trip.lastError}</span> : null}
            </div>

            {trip.destAddressLine ? (
              <div className="text-[11px] text-indigo-700 mt-0.5">{trip.destAddressLine}</div>
            ) : null}

            {trip.destSource ? (
              <div className="text-[10px] text-indigo-600 mt-0.5 opacity-80">Source: {trip.destSource}</div>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleTripPause}
              className="text-xs px-3 py-1 rounded-lg border border-indigo-300 text-indigo-900 bg-white hover:bg-indigo-100"
            >
              {trip.paused ? 'Resume' : 'Pause'}
            </button>
            <button
              onClick={() => stopTripInternal('manual_stop')}
              className="text-xs px-3 py-1 rounded-lg border border-indigo-300 text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Stop
            </button>
          </div>
        </div>
      ) : null}

      {tripToast ? (
        <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 font-semibold">
          {tripToast}
        </div>
      ) : null}

      {loading ? (
        <div className="p-6 text-gray-400 text-center">Loading...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-gray-500 mt-6">No visits found for today.</div>
      ) : (
        Object.entries(grouped).map(([date, group]) => (
          <div key={date} className="mb-6 shadow rounded-lg bg-white border">
            <div className="font-bold text-base px-4 py-3 bg-gray-50 rounded-t-lg flex items-center">
              <span>{date}</span>
              <span className="ml-3 bg-blue-200 text-blue-900 text-xs px-2 py-0.5 rounded-full">
                {group.length} items
              </span>
            </div>

            <div className="overflow-x-auto hidden sm:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-2 py-1">Student / Work</th>
                    <th className="px-2 py-1">Time</th>
                    <th className="px-2 py-1 w-40">Status</th>
                    <th className="px-2 py-1">Check-in</th>
                    <th className="px-2 py-1">Checkout</th>
                    <th className="px-2 py-1">G-sign</th>
                    <th className="px-2 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {group.map((v) => {
                    const work = isWorkItem(v);
                    const { rowClass, hideActions, reassigned, status, visitDateStr, isCanceled } = getRowClass(
                      v,
                      serverDateStr
                    );

                    const pendingCount = work ? pendingByWork?.[v.id] || 0 : pendingByVisit?.[v.id] || 0;
                    const showPendingBadge = offlineQueueEnabled && pendingCount > 0;

                    if (work) {
                      const from = v.scheduled_time || normalizeHHMM(v.from_hhmm) || normalizeHHMM(v.scheduled_at);
                      const to =
                        v.to_hhmm ||
                        normalizeHHMM(v.end_time) ||
                        (v.event_end_time ? String(v.event_end_time).slice(0, 5) : '');
                      const workType = String(v.work_type || 'other');
                      const titleText = `Work: ${workType}`;
                      const orgLocName = getOrgLocNameForWork(v, orgLocationsMap);

                      const hasCheckin = !!(v.checkin_hhmm || v.checkin_time_local || v.checkin_time);
                      const hasCheckout = !!(v.checkout_hhmm || v.checkout_time_local || v.checkout_time);

                      let checkinContent = null;
                      let checkoutContent = null;
                      let gsignContent = <span className="text-xs text-gray-400">N/A</span>;
                      let actionsContent = null;

                      if (isCanceled) {
                        const exp = <span className="italic text-xs text-gray-400">Expired</span>;
                        checkinContent = exp;
                        checkoutContent = exp;
                        gsignContent = exp;
                      } else if (visitDateStr < serverDateStr && v.status === 'draft') {
                        const exp = <span className="italic text-xs text-gray-400">Expired</span>;
                        checkinContent = exp;
                        checkoutContent = exp;
                        gsignContent = exp;
                      } else if (visitDateStr > serverDateStr) {
                        const wait = <span className="italic text-xs text-yellow-500">Waiting</span>;
                        checkinContent = wait;
                        checkoutContent = wait;
                        gsignContent = wait;
                      }

                      if (!checkinContent) {
                        if (hasCheckin) {
                          const timeText =
                            v.checkin_time_local || (v.checkin_time ? String(v.checkin_time).slice(11, 16) : '');
                          checkinContent = <span className="text-sm">{timeText ? `${timeText}` : ''}</span>;
                        } else if (!hideActions) {
                          const disabled = offlineBlocked;
                          checkinContent = (
                            <button
                              onClick={() => handleWorkCheckin(v.id)}
                              disabled={disabled}
                              className={`text-white px-2 py-1 rounded text-xs ${
                                disabled
                                  ? 'bg-gray-300 cursor-not-allowed'
                                  : isOnline
                                  ? 'bg-blue-500 hover:bg-blue-600'
                                  : 'bg-amber-500 hover:bg-amber-600'
                              }`}
                            >
                              {disabled ? 'Offline' : isOnline ? 'Check-in' : 'Check-in (sync later)'}
                            </button>
                          );
                        }
                      }

                      if (!checkoutContent) {
                        if (hasCheckout) {
                          const timeText =
                            v.checkout_time_local || (v.checkout_time ? String(v.checkout_time).slice(11, 16) : '');
                          checkoutContent = <span className="text-sm">{timeText ? `${timeText}` : ''}</span>;
                        } else if (!hideActions && hasCheckin) {
                          const disabled = offlineBlocked;
                          checkoutContent = (
                            <button
                              onClick={() => handleWorkCheckout(v.id)}
                              disabled={disabled}
                              className={`text-white px-2 py-1 rounded text-xs ${
                                disabled
                                  ? 'bg-gray-300 cursor-not-allowed'
                                  : isOnline
                                  ? 'bg-green-500 hover:bg-green-600'
                                  : 'bg-amber-500 hover:bg-amber-600'
                              }`}
                            >
                              {disabled ? 'Offline' : isOnline ? 'Checkout' : 'Checkout (sync later)'}
                            </button>
                          );
                        }
                      }

                      if (!hideActions) {
                        actionsContent = (
                          <div className="flex flex-col gap-1 w-full max-w-full min-w-0">
                            {canCancelWork(v, serverDateStr) ? (
                              <button
                                onClick={() => setCancelWorkId(v.id)}
                                disabled={offlineBlocked}
                                className={`text-white px-2 py-1 rounded text-xs ${
                                  offlineBlocked
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : isOnline
                                    ? 'bg-purple-700 hover:bg-purple-800'
                                    : 'bg-amber-500 hover:bg-amber-600'
                                }`}
                              >
                                {offlineBlocked ? 'Offline' : isOnline ? 'Cancel' : 'Cancel (sync later)'}
                              </button>
                            ) : null}
                          </div>
                        );
                      }

                      const workCanceledReason = String(v.canceled_reason || '').trim();

                      return (
                        <tr key={`work_${v.id}`} className={rowClass}>
                          <td className="px-2 py-1">
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Building2 className="w-4 h-4 text-slate-600" />
                                <span className="font-semibold text-sky-900">{titleText}</span>

                                <button
                                  onClick={() => openWorkNotesModal(v)}
                                  className="text-purple-700 hover:text-purple-900"
                                  title="Show work notes"
                                >
                                  <FaBookOpen size={18} />
                                </button>

                                <button
                                  onClick={() => openWorkLocationModal(v)}
                                  className="text-gray-400 hover:text-blue-600"
                                  title="Show work location on map"
                                >
                                  <MapPin size={18} />
                                </button>

                                <button
                                  onClick={async () => {
                                    try {
                                      if (!isOnline) return;
                                      const { lat, lon } = await resolveWorkDestination(v);
                                      openGoogleMapsToLatLon(lat, lon);
                                    } catch {
                                      alert('Work destination is missing coordinates.');
                                    }
                                  }}
                                  className="text-green-600 hover:text-green-800"
                                  title={isOnline ? 'Open navigation (Google Maps)' : 'Offline'}
                                >
                                  <SiGooglemaps size={18} />
                                </button>

                                {(() => {
                                  const disabledTrip = !canStartTripForWork(v, serverDateStr);
                                  return (
                                    <button
                                      onClick={async () => {
                                        if (disabledTrip) return;
                                        try {
                                          const { lat, lon } = await resolveWorkDestination(v);
                                          const label = `Work: ${String(v.work_type || 'other')}`;

                                          const okToSwitch = await confirmSwitchTripIfNeeded({
                                            dest_kind: 'work',
                                            dest_id: v.id,
                                            destLabel: label,
                                          });
                                          if (!okToSwitch) return;

                                          const okStart = await confirmStartTrip({ label });
                                          if (!okStart) return;

                                          await startTrip({
                                            dest_kind: 'work',
                                            dest_id: v.id,
                                            destLabel: label,
                                            destLat: lat,
                                            destLon: lon,
                                          });
                                        } catch {
                                          alert('Work destination is missing coordinates.');
                                        }
                                      }}
                                      disabled={disabledTrip}
                                      className={`ml-1 ${
                                        disabledTrip ? 'text-gray-300 cursor-not-allowed' : 'text-indigo-700 hover:text-indigo-900'
                                      }`}
                                      title={disabledTrip ? 'Trip tracking disabled for completed/canceled work' : 'Start trip tracking'}
                                    >
                                      <Car size={18} />
                                    </button>
                                  );
                                })()}
                              </div>

                              {String(v.location_mode || '') === 'org_location' ? (
                                <div className="mt-0.5 text-[11px] text-slate-500">
                                  {orgLocName ? (
                                    <>
                                      <span className="font-semibold">Location:</span> {orgLocName}
                                    </>
                                  ) : (
                                    <span className="italic">Location</span>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          </td>

                          <td className="px-2 py-1">
                            <VisitTimeType
                              scheduled_time={from}
                              event_end_time={to ? `${String(to).slice(0, 5)}:00` : null}
                            />
                          </td>

                          <td className="px-2 py-1 flex flex-col">
                            <div className="flex items-center gap-2 flex-wrap">
                              <StatusBadge status={v.status}>{v.status === 'canceled' ? 'Canceled' : v.status}</StatusBadge>

                              {showPendingBadge ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                                  Pending sync ({pendingCount})
                                </span>
                              ) : null}
                            </div>

                            {v.status === 'canceled' && workCanceledReason ? (
                              <span className="mt-1 text-xs text-purple-700 italic">{workCanceledReason}</span>
                            ) : null}
                          </td>

                          <td className="px-2 py-1">{checkinContent}</td>
                          <td className="px-2 py-1">{checkoutContent}</td>
                          <td className="px-2 py-1">{gsignContent}</td>
                          <td className="px-2 py-1">{actionsContent}</td>
                        </tr>
                      );
                    }

                    const studentName = `${v.student_last_name || ''}${v.student_last_name ? ', ' : ''}${v.student_first_name || ''}`.trim();

                    const isTraineeVisitForCurrentTeacher =
                      v.trainee_teacher_id && currentTeacherId && Number(v.trainee_teacher_id) === Number(currentTeacherId);

                    const isSupervisorVisitForCurrentTeacher =
                      !isTraineeVisitForCurrentTeacher &&
                      v.trainee_teacher_id &&
                      currentTeacherId &&
                      Number(v.teacher_id) === Number(currentTeacherId);

                    const isTrainingVisitForCurrentTeacher = isTraineeVisitForCurrentTeacher || isSupervisorVisitForCurrentTeacher;

                    const isReadOnlyTrainingVisit =
                      isTraineeVisitForCurrentTeacher && (trainingStage === 'shadow' || trainingStage === 'supervised');

                    let trainingType = null;
                    if (isTraineeVisitForCurrentTeacher && (trainingStage === 'shadow' || trainingStage === 'supervised')) {
                      trainingType = trainingStage;
                    } else if (isSupervisorVisitForCurrentTeacher && v.trainee_teacher_id) {
                      trainingType = 'training';
                    }

                    const hasCheckin = !!v.checkin_time;
                    const hasCheckout = !!v.checkout_time;
                    const telehealth = isTelehealthVisit(v);
                    const hideActionsForRole = hideActions || isReadOnlyTrainingVisit;
                    const eventFlag = isEventVisit(v);

                    let checkinContent = null;
                    let checkoutContent = null;
                    let gsignContent = null;
                    let editBtnsContent = null;

                    if (isCanceled) {
                      const exp = <span className="italic text-xs text-gray-400">Expired</span>;
                      checkinContent = exp;
                      checkoutContent = exp;
                      gsignContent = exp;
                    } else if (visitDateStr < serverDateStr && v.status === 'draft') {
                      const exp = <span className="italic text-xs text-gray-400">Expired</span>;
                      checkinContent = exp;
                      checkoutContent = exp;
                      gsignContent = exp;
                    } else if (visitDateStr > serverDateStr) {
                      const wait = <span className="italic text-xs text-yellow-500">Waiting</span>;
                      checkinContent = wait;
                      checkoutContent = wait;
                      gsignContent = wait;
                    }

                    if (!checkinContent) {
                      if (hasCheckin) {
                        if (telehealth) {
                          const timeText =
                            v.checkin_time_local || (v.checkin_time ? v.checkin_time.slice(11, 16) : '');
                          checkinContent = (
                            <div className="flex flex-col items-start gap-1">
                              <span className="text-xs text-sky-800 font-semibold">Started at {timeText}</span>
                              {v.telehealth_link && (
                                <a
                                  href={v.telehealth_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900 underline"
                                >
                                  <FiLink2 className="w-3 h-3" />
                                  Open telehealth
                                </a>
                              )}
                            </div>
                          );
                        } else {
                          checkinContent = v.checkin_time_local || (
                            <span className="text-xs text-green-700">Checked in</span>
                          );
                        }
                      } else if (!hideActionsForRole) {
                        const disabled = offlineBlocked;
                        if (telehealth) {
                          checkinContent = (
                            <button
                              onClick={() => handleCheckin(v.id)}
                              disabled={disabled}
                              className={`inline-flex items-center gap-1 text-white px-2 py-1 rounded text-xs ${
                                disabled
                                  ? 'bg-gray-300 cursor-not-allowed'
                                  : isOnline
                                  ? 'bg-sky-500 hover:bg-sky-600'
                                  : 'bg-amber-500 hover:bg-amber-600'
                              }`}
                            >
                              <FiVideo className="w-3 h-3" />
                              {disabled ? 'Offline' : isOnline ? 'Start Telehealth' : 'Start (sync later)'}
                            </button>
                          );
                        } else {
                          checkinContent = (
                            <button
                              onClick={() => handleCheckin(v.id)}
                              disabled={disabled}
                              className={`text-white px-2 py-1 rounded text-xs ${
                                disabled
                                  ? 'bg-gray-300 cursor-not-allowed'
                                  : isOnline
                                  ? 'bg-blue-500 hover:bg-blue-600'
                                  : 'bg-amber-500 hover:bg-amber-600'
                              }`}
                            >
                              {disabled ? 'Offline' : isOnline ? 'Check-in' : 'Check-in (sync later)'}
                            </button>
                          );
                        }
                      } else if (isReadOnlyTrainingVisit) {
                        checkinContent = (
                          <span className="text-xs text-teal-700">Training visit – supervisor handles check-in</span>
                        );
                      }
                    }

                    if (!checkoutContent) {
                      if (hasCheckout) {
                        if (telehealth) {
                          const timeText =
                            v.checkout_time_local || (v.checkout_time ? v.checkout_time.slice(11, 16) : '');
                          checkoutContent = <span className="text-xs text-sky-800">Ended at {timeText}</span>;
                        } else {
                          checkoutContent = v.checkout_time_local || (
                            <span className="text-xs text-green-700">Checked out</span>
                          );
                        }
                      } else if (!hideActionsForRole && hasCheckin) {
                        const disabled = offlineBlocked;
                        if (telehealth) {
                          checkoutContent = (
                            <button
                              onClick={() => handleCheckout(v)}
                              disabled={disabled}
                              className={`inline-flex items-center gap-1 text-white px-2 py-1 rounded text-xs ${
                                disabled
                                  ? 'bg-gray-300 cursor-not-allowed'
                                  : isOnline
                                  ? 'bg-sky-500 hover:bg-sky-600'
                                  : 'bg-amber-500 hover:bg-amber-600'
                              }`}
                            >
                              <FiVideoOff className="w-3 h-3" />
                              {disabled ? 'Offline' : isOnline ? 'End Telehealth' : 'End (sync later)'}
                            </button>
                          );
                        } else {
                          checkoutContent = (
                            <button
                              onClick={() => handleCheckout(v)}
                              disabled={disabled}
                              className={`text-white px-2 py-1 rounded text-xs ${
                                disabled
                                  ? 'bg-gray-300 cursor-not-allowed'
                                  : isOnline
                                  ? 'bg-green-500 hover:bg-green-600'
                                  : 'bg-amber-500 hover:bg-amber-600'
                              }`}
                            >
                              {disabled ? 'Offline' : isOnline ? 'Checkout' : 'Checkout (sync later)'}
                            </button>
                          );
                        }
                      } else if (isReadOnlyTrainingVisit && hasCheckin) {
                        checkoutContent = (
                          <span className="text-xs text-teal-700">Training visit – supervisor completes checkout</span>
                        );
                      }
                    }

                    if (!gsignContent && !hideActionsForRole) {
                      if (telehealth) {
                        gsignContent = <span className="text-xs text-gray-400">N/A</span>;
                      } else {
                        gsignContent = v.g_sign_confirmed ? '✅' : '❌';
                      }
                    } else if (!gsignContent && isReadOnlyTrainingVisit) {
                      gsignContent = (
                        <span className="text-xs text-teal-700">Training visit – G-sign by supervisor</span>
                      );
                    }

                    if (!hideActionsForRole) {
                      const canShowQR = !telehealth && !v.g_sign_confirmed;
                      const canEditNotes = hasCheckin || hasCheckout;

                      editBtnsContent = (
                        <div className="flex flex-col gap-1 w-full max-w-full min-w-0">
                          {canEditNotes && (
                            <button
                              onClick={() => openNotesModal(v, 'edit')}
                              disabled={offlineBlocked}
                              className={`text-white px-2 py-1 rounded text-xs ${
                                offlineBlocked
                                  ? 'bg-gray-300 cursor-not-allowed'
                                  : isOnline
                                  ? 'bg-yellow-500 hover:bg-yellow-600'
                                  : 'bg-amber-500 hover:bg-amber-600'
                              }`}
                            >
                              {offlineBlocked ? 'Offline' : 'Edit Notes'}
                            </button>
                          )}
                          {canShowQR && (
                            <button
                              onClick={() => {
                                setQrModalVisitId(v.id);
                                setVisitConfirmed(false);
                              }}
                              className="bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded text-xs"
                              disabled={!isOnline}
                              title={!isOnline ? 'Offline: QR status cannot be refreshed' : 'Show QR'}
                            >
                              Show QR
                            </button>
                          )}
                          {canCancel(v) && (
                            <button
                              onClick={() => setCancelVisitId(v.id)}
                              disabled={offlineBlocked}
                              className={`text-white px-2 py-1 rounded text-xs ${
                                offlineBlocked
                                  ? 'bg-gray-300 cursor-not-allowed'
                                  : isOnline
                                  ? 'bg-purple-700 hover:bg-purple-800'
                                  : 'bg-amber-500 hover:bg-amber-600'
                              }`}
                            >
                              {offlineBlocked ? 'Offline' : isOnline ? 'Cancel' : 'Cancel (sync later)'}
                            </button>
                          )}
                        </div>
                      );
                    }

                    const reminderExists = hasReminder(v.student_id);
                    const reminderPreview = reminderExists ? reminderPreview25(v.student_id) : '';

                    const locMode = String(v.location_mode || 'at_home');
                    const locIcon =
                      locMode === 'at_clinic' ? (
                        <Building2 className="w-4 h-4 text-slate-600" />
                      ) : (
                        <Home className="w-4 h-4 text-slate-600" />
                      );

                    return (
                      <tr key={v.id} className={rowClass}>
                        <td className="px-2 py-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-1">{locIcon}</span>

                            <span
                              onClick={() => openStudentModal(v.student_id)}
                              className="text-blue-700 underline cursor-pointer hover:text-blue-900"
                            >
                              {studentName}
                            </span>

                            {eventFlag ? (
                              <EventBadge isEvent={true} />
                            ) : (
                              <ProgramBadge program_type={v.program_type} discipline_code={v.discipline_code} />
                            )}

                            <button
                              onClick={() => openMapModalFunc(v)}
                              className="ml-1 text-gray-400 hover:text-blue-600"
                              title={isOnline ? 'Show on Map' : 'Offline'}
                            >
                              <MapPin size={16} />
                            </button>

                            <button
                              onClick={() => openGoogleMapsDirections(v.student_id, v.teacher_id, v)}
                              className="ml-1 text-green-600 hover:text-green-800"
                              title={isOnline ? 'Open navigation (Google Maps)' : 'Offline'}
                            >
                              <SiGooglemaps size={16} />
                            </button>

                            <button
                              onClick={() => openNotesHistoryModal(v.student_id, studentName)}
                              className="ml-1 text-purple-700 hover:text-purple-900"
                              title={isOnline ? 'Show Notes History' : 'Offline'}
                            >
                              <FaBookOpen size={16} />
                            </button>

                            {(() => {
                              const disabledTrip = !canStartTripForVisit(v, serverDateStr);
                              const showAiGuidance = canShowAiGuidanceForVisit(v);
                              const guidanceIconClass = aiAccess.loading
                                ? 'text-gray-300 cursor-not-allowed'
                                : canUseAiGuidance
                                ? 'text-sky-600 hover:text-sky-800'
                                : 'text-amber-600 hover:text-amber-800';

                              const guidanceTitle = aiAccess.loading
                                ? 'Checking AI access...'
                                : canUseAiGuidance
                                ? 'AI visit guidance'
                                : getAiDisabledMessage('visit_guidance');

                              return (
                                <>
                                  <button
                                    onClick={async () => {
                                      if (disabledTrip) return;
                                      try {
                                        const { lat, lon, label } = await getVisitDestination(v);

                                        const okToSwitch = await confirmSwitchTripIfNeeded({
                                          dest_kind: 'visit',
                                          dest_id: v.id,
                                          destLabel: label,
                                        });
                                        if (!okToSwitch) return;

                                        const okStart = await confirmStartTrip({ label });
                                        if (!okStart) return;

                                        await startTrip({
                                          dest_kind: 'visit',
                                          dest_id: v.id,
                                          destLabel: label,
                                          destLat: lat,
                                          destLon: lon,
                                        });
                                      } catch {
                                        alert('Destination is missing coordinates.');
                                      }
                                    }}
                                    disabled={disabledTrip}
                                    className={`ml-1 ${
                                      disabledTrip ? 'text-gray-300 cursor-not-allowed' : 'text-indigo-700 hover:text-indigo-900'
                                    }`}
                                    title={disabledTrip ? 'Trip tracking disabled for completed/canceled visit' : 'Start trip tracking'}
                                  >
                                    <Car size={18} />
                                  </button>

                                  {showAiGuidance ? (
                                    <button
                                      onClick={() => openAiGuidanceModal(v)}
                                      className={`ml-1 ${guidanceIconClass}`}
                                      title={guidanceTitle}
                                    >
                                      <Sparkles size={16} />
                                    </button>
                                  ) : null}
                                </>
                              );
                            })()}

                            <button
                              onClick={() => openReminderModal(v.student_id, studentName)}
                              className={`ml-1 ${
                                reminderExists
                                  ? 'text-yellow-600 hover:text-yellow-800'
                                  : 'text-gray-400 hover:text-gray-600'
                              }`}
                              title={reminderExists ? 'Edit student reminder' : 'Add student reminder'}
                            >
                              {reminderExists ? <FaStickyNote size={16} /> : <FaRegStickyNote size={16} />}
                            </button>
                          </div>

                          {String(v.location_mode || '').toLowerCase() === 'at_clinic' ? (
                            <div className="mt-0.5 text-[11px] text-slate-500">
                              {getClinicNameForVisit(v, orgLocationsMap) ? (
                                <>
                                  <span className="font-semibold">Clinic:</span> {getClinicNameForVisit(v, orgLocationsMap)}
                                </>
                              ) : (
                                <span className="italic">Clinic</span>
                              )}
                            </div>
                          ) : null}

                          <TelehealthBadge delivery_mode={v.delivery_mode} />

                          {reminderExists && reminderPreview ? (
                            <div className="mt-1 text-[11px] text-yellow-800 bg-yellow-50 border border-yellow-200 rounded px-2 py-1 inline-block">
                              <span className="font-semibold">Reminder:</span> {reminderPreview}
                            </div>
                          ) : null}
                        </td>

                        <td className="px-2 py-1">
                          <VisitTimeType scheduled_time={v.scheduled_time} event_end_time={v.event_end_time} />
                        </td>

                        <td className="px-2 py-1 flex flex-col">
                          <div className="flex items-center gap-2 flex-wrap">
                            <StatusBadge status={v.status}>{v.status === 'canceled' ? 'Canceled' : v.status}</StatusBadge>

                            {showPendingBadge ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                                Pending sync ({pendingCount})
                              </span>
                            ) : null}
                          </div>

                          {isTrainingVisitForCurrentTeacher && <TrainingBadge trainingType={trainingType} />}

                          {isSupervisorVisitForCurrentTeacher && v.trainee_teacher_id && (
                            <button
                              type="button"
                              onClick={() => setMentorFeedback({ open: true, visit: v })}
                              className="mt-1 self-start px-2 py-0.5 rounded text-[11px] bg-blue-50 text-blue-700 border border-blue-300 hover:bg-blue-100"
                            >
                              Mentor feedback
                            </button>
                          )}

                          {isCanceled && v.notes && <span className="mt-1 text-xs text-purple-700 italic">{v.notes}</span>}

                          {reassigned && status && !isCanceled && (
                            <span className={`mt-1 text-xs ${status === 'rejected' ? 'text-red-700' : 'text-orange-700'}`}>
                              Substitute: {status}
                            </span>
                          )}
                        </td>

                        <td className="px-2 py-1">{checkinContent}</td>
                        <td className="px-2 py-1">{checkoutContent}</td>
                        <td className="px-2 py-1">{gsignContent}</td>
                        <td className="px-2 py-1">{editBtnsContent}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="sm:hidden flex flex-col gap-2 p-2 overflow-x-hidden">
              {group.map((v) => {
                const work = isWorkItem(v);
                const { rowClass, hideActions, reassigned, status, visitDateStr, isCanceled } = getRowClass(
                  v,
                  serverDateStr
                );

                const btnBase = 'px-3 py-1.5 rounded-md text-xs font-semibold w-full leading-tight';
                const btnDisabled = 'bg-gray-300 cursor-not-allowed text-white';
                const btnBlue = 'bg-blue-500 hover:bg-blue-600 text-white';
                const btnGreen = 'bg-green-500 hover:bg-green-600 text-white';
                const btnYellow = 'bg-yellow-500 hover:bg-yellow-600 text-white';
                const btnGray = 'bg-gray-500 hover:bg-gray-600 text-white';
                const btnPurple = 'bg-purple-700 hover:bg-purple-800 text-white';
                const btnAmber = 'bg-amber-500 hover:bg-amber-600 text-white';

                const pendingCount = work ? pendingByWork?.[v.id] || 0 : pendingByVisit?.[v.id] || 0;
                const showPendingBadge = offlineQueueEnabled && pendingCount > 0;

                if (work) {
                  const from = v.scheduled_time || normalizeHHMM(v.from_hhmm) || normalizeHHMM(v.scheduled_at);
                  const to =
                    v.to_hhmm ||
                    normalizeHHMM(v.end_time) ||
                    (v.event_end_time ? String(v.event_end_time).slice(0, 5) : '');
                  const workType = String(v.work_type || 'other');

                  const titleText = `Work: ${workType}`;
                  const orgLocName = getOrgLocNameForWork(v, orgLocationsMap);

                  const hasCheckin = !!(v.checkin_hhmm || v.checkin_time_local || v.checkin_time);
                  const hasCheckout = !!(v.checkout_hhmm || v.checkout_time_local || v.checkout_time);

                  let checkinContent = null;
                  let checkoutContent = null;

                  if (isCanceled) {
                    const exp = <span className="italic text-xs text-gray-400">Expired</span>;
                    checkinContent = exp;
                    checkoutContent = exp;
                  } else if (visitDateStr < serverDateStr && v.status === 'draft') {
                    const exp = <span className="italic text-xs text-gray-400">Expired</span>;
                    checkinContent = exp;
                    checkoutContent = exp;
                  } else if (visitDateStr > serverDateStr) {
                    const wait = <span className="italic text-xs text-yellow-500">Waiting</span>;
                    checkinContent = wait;
                    checkoutContent = wait;
                  }

                  if (!checkinContent) {
                    if (hasCheckin) {
                      const timeText =
                        v.checkin_time_local || (v.checkin_time ? String(v.checkin_time).slice(11, 16) : '');
                      checkinContent = v.checkin_time_local ? (
                        <span className="text-base text-slate-900 leading-none">{timeText}</span>
                      ) : (
                        <span className="text-xs text-green-700">Checked in</span>
                      );
                    } else if (!hideActions) {
                      const disabled = offlineBlocked;
                      checkinContent = (
                        <button
                          onClick={() => handleWorkCheckin(v.id)}
                          disabled={disabled}
                          className={`${btnBase} ${disabled ? btnDisabled : isOnline ? btnBlue : btnAmber}`}
                        >
                          {disabled ? 'Offline' : isOnline ? 'Check-in' : 'Check-in (sync later)'}
                        </button>
                      );
                    }
                  }

                  if (!checkoutContent) {
                    if (hasCheckout) {
                      const timeText =
                        v.checkout_time_local || (v.checkout_time ? String(v.checkout_time).slice(11, 16) : '');
                      checkoutContent = v.checkout_time_local ? (
                        <span className="text-base text-slate-900 leading-none">{timeText}</span>
                      ) : (
                        <span className="text-xs text-green-700">Checked out</span>
                      );
                    } else if (!hideActions && hasCheckin) {
                      const disabled = offlineBlocked;
                      checkoutContent = (
                        <button
                          onClick={() => handleWorkCheckout(v.id)}
                          disabled={disabled}
                          className={`${btnBase} ${disabled ? btnDisabled : isOnline ? btnGreen : btnAmber}`}
                        >
                          {disabled ? 'Offline' : isOnline ? 'Checkout' : 'Checkout (sync later)'}
                        </button>
                      );
                    }
                  }

                  const workCanceledReason = String(v.canceled_reason || '').trim();

                  return (
                    <div key={`work_${v.id}`} className={`rounded-lg border p-3 overflow-hidden min-w-0 bg-white ${rowClass}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <Building2 className="w-4 h-4 text-slate-600 shrink-0" />
                            <span className="font-semibold text-sky-900 truncate">{titleText}</span>

                            <button
                              onClick={() => openWorkNotesModal(v)}
                              className="text-purple-700 hover:text-purple-900"
                              title="Show work notes"
                            >
                              <FaBookOpen size={18} />
                            </button>

                            <button
                              onClick={() => openWorkLocationModal(v)}
                              className="text-gray-400 hover:text-blue-600"
                              title="Show work location on map"
                            >
                              <MapPin size={18} />
                            </button>

                            <button
                              onClick={async () => {
                                try {
                                  if (!isOnline) return;
                                  const { lat, lon } = await resolveWorkDestination(v);
                                  openGoogleMapsToLatLon(lat, lon);
                                } catch {
                                  alert('Work destination is missing coordinates.');
                                }
                              }}
                              className="text-green-600 hover:text-green-800"
                              title={isOnline ? 'Open navigation (Google Maps)' : 'Offline'}
                            >
                              <SiGooglemaps size={18} />
                            </button>

                            {(() => {
                              const disabledTrip = !canStartTripForWork(v, serverDateStr);
                              return (
                                <button
                                  onClick={async () => {
                                    if (disabledTrip) return;
                                    try {
                                      const { lat, lon } = await resolveWorkDestination(v);
                                      const label = `Work: ${String(v.work_type || 'other')}`;

                                      const okToSwitch = await confirmSwitchTripIfNeeded({
                                        dest_kind: 'work',
                                        dest_id: v.id,
                                        destLabel: label,
                                      });
                                      if (!okToSwitch) return;

                                      const okStart = await confirmStartTrip({ label });
                                      if (!okStart) return;

                                      await startTrip({
                                        dest_kind: 'work',
                                        dest_id: v.id,
                                        destLabel: label,
                                        destLat: lat,
                                        destLon: lon,
                                      });
                                    } catch {
                                      alert('Work destination is missing coordinates.');
                                    }
                                  }}
                                  disabled={disabledTrip}
                                  className={`ml-1 ${
                                    disabledTrip ? 'text-gray-300 cursor-not-allowed' : 'text-indigo-700 hover:text-indigo-900'
                                  }`}
                                  title={disabledTrip ? 'Trip tracking disabled for completed/canceled work' : 'Start trip tracking'}
                                >
                                  <Car size={20} />
                                </button>
                              );
                            })()}
                          </div>

                          {String(v.location_mode || '') === 'org_location' ? (
                            <div className="mt-0.5 text-[11px] text-slate-500">
                              {orgLocName ? (
                                <>
                                  <span className="font-semibold">Location:</span> {orgLocName}
                                </>
                              ) : (
                                <span className="italic">Location</span>
                              )}
                            </div>
                          ) : null}

                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <StatusBadge status={v.status}>{v.status === 'canceled' ? 'Canceled' : v.status}</StatusBadge>
                            {showPendingBadge ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                                Pending sync ({pendingCount})
                              </span>
                            ) : null}
                            <span className="text-xs text-gray-400">G-sign: N/A</span>
                          </div>

                          {v.status === 'canceled' && workCanceledReason ? (
                            <div className="mt-2 text-xs text-purple-700 italic">{workCanceledReason}</div>
                          ) : null}
                        </div>

                        <div className="shrink-0 text-right">
                          <VisitTimeType scheduled_time={from} event_end_time={to ? `${String(to).slice(0, 5)}:00` : null} />
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2 items-start">
                        <div className="min-w-0">
                          <div className="text-xs text-slate-600">
                            <div className="font-semibold text-slate-700 mb-1">Check-in</div>
                            <div className="min-w-0">{checkinContent}</div>
                          </div>

                          <div className="mt-3 text-xs text-slate-600">
                            <div className="font-semibold text-slate-700 mb-1">G-sign</div>
                            <div className="text-base leading-none">N/A</div>
                          </div>
                        </div>

                        <div className="min-w-0">
                          <div className="text-xs text-slate-600">
                            <div className="font-semibold text-slate-700 mb-1">Checkout</div>
                            <div className="min-w-0">{checkoutContent || <span className="text-xs text-slate-300">—</span>}</div>
                          </div>

                          {!hideActions ? (
                            <div className="mt-2 flex flex-col gap-2">
                              {canCancelWork(v, serverDateStr) ? (
                                <button
                                  onClick={() => setCancelWorkId(v.id)}
                                  disabled={offlineBlocked}
                                  className={`${btnBase} ${offlineBlocked ? btnDisabled : isOnline ? btnPurple : btnAmber}`}
                                >
                                  {offlineBlocked ? 'Offline' : isOnline ? 'Cancel' : 'Cancel (sync later)'}
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                }

                const studentName = `${v.student_last_name || ''}${v.student_last_name ? ', ' : ''}${v.student_first_name || ''}`.trim();

                const isTraineeVisitForCurrentTeacher =
                  v.trainee_teacher_id && currentTeacherId && Number(v.trainee_teacher_id) === Number(currentTeacherId);

                const isSupervisorVisitForCurrentTeacher =
                  !isTraineeVisitForCurrentTeacher &&
                  v.trainee_teacher_id &&
                  currentTeacherId &&
                  Number(v.teacher_id) === Number(currentTeacherId);

                const isTrainingVisitForCurrentTeacher = isTraineeVisitForCurrentTeacher || isSupervisorVisitForCurrentTeacher;

                const isReadOnlyTrainingVisit =
                  isTraineeVisitForCurrentTeacher && (trainingStage === 'shadow' || trainingStage === 'supervised');

                let trainingType = null;
                if (isTraineeVisitForCurrentTeacher && (trainingStage === 'shadow' || trainingStage === 'supervised')) {
                  trainingType = trainingStage;
                } else if (isSupervisorVisitForCurrentTeacher && v.trainee_teacher_id) {
                  trainingType = 'training';
                }

                const hasCheckin = !!v.checkin_time;
                const hasCheckout = !!v.checkout_time;
                const telehealth = isTelehealthVisit(v);

                const hideActionsForRole = hideActions || isReadOnlyTrainingVisit;
                const eventFlag = isEventVisit(v);

                let checkinContent = null;
                let checkoutContent = null;
                let gsignContent = null;

                if (isCanceled) {
                  const exp = <span className="italic text-xs text-gray-400">Expired</span>;
                  checkinContent = exp;
                  checkoutContent = exp;
                  gsignContent = exp;
                } else if (visitDateStr < serverDateStr && v.status === 'draft') {
                  const exp = <span className="italic text-xs text-gray-400">Expired</span>;
                  checkinContent = exp;
                  checkoutContent = exp;
                  gsignContent = exp;
                } else if (visitDateStr > serverDateStr) {
                  const wait = <span className="italic text-xs text-yellow-500">Waiting</span>;
                  checkinContent = wait;
                  checkoutContent = wait;
                  gsignContent = wait;
                }

                if (!checkinContent) {
                  if (hasCheckin) {
                    if (telehealth) {
                      const timeText = v.checkin_time_local || (v.checkin_time ? v.checkin_time.slice(11, 16) : '');
                      checkinContent = (
                        <div className="flex flex-col items-start gap-1">
                          <span className="text-xs text-sky-800 font-semibold">Started at {timeText}</span>
                          {v.telehealth_link && (
                            <a
                              href={v.telehealth_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900 underline"
                            >
                              <FiLink2 className="w-3 h-3" />
                              Open telehealth
                            </a>
                          )}
                        </div>
                      );
                    } else {
                      checkinContent = v.checkin_time_local ? (
                        <span className="text-base text-slate-900 leading-none">{v.checkin_time_local}</span>
                      ) : (
                        <span className="text-xs text-green-700">Checked in</span>
                      );
                    }
                  } else if (!hideActionsForRole) {
                    const disabled = offlineBlocked;
                    checkinContent = (
                      <button
                        onClick={() => handleCheckin(v.id)}
                        disabled={disabled}
                        className={`${btnBase} ${disabled ? btnDisabled : isOnline ? btnBlue : btnAmber}`}
                      >
                        {disabled
                          ? 'Offline'
                          : telehealth
                          ? isOnline
                            ? 'Start Telehealth'
                            : 'Start (sync later)'
                          : isOnline
                          ? 'Check-in'
                          : 'Check-in (sync later)'}
                      </button>
                    );
                  } else if (isReadOnlyTrainingVisit) {
                    checkinContent = <span className="text-xs text-teal-700">Training visit – supervisor handles check-in</span>;
                  }
                }

                if (!checkoutContent) {
                  if (hasCheckout) {
                    if (telehealth) {
                      const timeText = v.checkout_time_local || (v.checkout_time ? v.checkout_time.slice(11, 16) : '');
                      checkoutContent = <span className="text-xs text-sky-800">Ended at {timeText}</span>;
                    } else {
                      checkoutContent = v.checkout_time_local ? (
                        <span className="text-base text-slate-900 leading-none">{v.checkout_time_local}</span>
                      ) : (
                        <span className="text-xs text-green-700">Checked out</span>
                      );
                    }
                  } else if (!hideActionsForRole && hasCheckin) {
                    const disabled = offlineBlocked;
                    checkoutContent = (
                      <button
                        onClick={() => handleCheckout(v)}
                        disabled={disabled}
                        className={`${btnBase} ${disabled ? btnDisabled : isOnline ? btnGreen : btnAmber}`}
                      >
                        {disabled
                          ? 'Offline'
                          : telehealth
                          ? isOnline
                            ? 'End Telehealth'
                            : 'End (sync later)'
                          : isOnline
                          ? 'Checkout'
                          : 'Checkout (sync later)'}
                      </button>
                    );
                  } else if (isReadOnlyTrainingVisit && hasCheckin) {
                    checkoutContent = <span className="text-xs text-teal-700">Training visit – supervisor completes checkout</span>;
                  }
                }

                if (!gsignContent && !hideActionsForRole) {
                  gsignContent = telehealth ? (
                    <span className="text-xs text-gray-400">N/A</span>
                  ) : v.g_sign_confirmed ? (
                    '✅'
                  ) : (
                    '❌'
                  );
                } else if (!gsignContent && isReadOnlyTrainingVisit) {
                  gsignContent = <span className="text-xs text-teal-700">Training visit – G-sign by supervisor</span>;
                }

                const canShowQR = !telehealth && !v.g_sign_confirmed;
                const canEditNotes = hasCheckin || hasCheckout;

                const reminderExists = hasReminder(v.student_id);
                const reminderPreview = reminderExists ? reminderPreview25(v.student_id) : '';

                const locMode = String(v.location_mode || 'at_home');
                const locIcon =
                  locMode === 'at_clinic' ? (
                    <Building2 className="w-4 h-4 text-slate-600" />
                  ) : (
                    <Home className="w-4 h-4 text-slate-600" />
                  );

                return (
                  <div key={v.id} className={`rounded-lg border p-3 overflow-hidden min-w-0 bg-white ${rowClass}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <span className="inline-flex items-center gap-1">{locIcon}</span>
                          <span
                            onClick={() => openStudentModal(v.student_id)}
                            className="text-blue-700 underline cursor-pointer hover:text-blue-900 font-semibold truncate"
                            title={studentName}
                          >
                            {studentName}
                          </span>

                          {eventFlag ? (
                            <EventBadge isEvent={true} />
                          ) : (
                            <ProgramBadge program_type={v.program_type} discipline_code={v.discipline_code} />
                          )}
                        </div>

                        {String(v.location_mode || '').toLowerCase() === 'at_clinic' ? (
                          <div className="mt-0.5 text-[11px] text-slate-500">
                            {getClinicNameForVisit(v, orgLocationsMap) ? (
                              <>
                                <span className="font-semibold">Clinic:</span> {getClinicNameForVisit(v, orgLocationsMap)}
                              </>
                            ) : (
                              <span className="italic">Clinic</span>
                            )}
                          </div>
                        ) : null}

                        <div className="mt-1 flex items-center gap-2">
                          <button
                            onClick={() => openMapModalFunc(v)}
                            className="text-gray-400 hover:text-blue-600"
                            title={isOnline ? 'Show on Map' : 'Offline'}
                          >
                            <MapPin size={18} />
                          </button>
                          <button
                            onClick={() => openGoogleMapsDirections(v.student_id, v.teacher_id, v)}
                            className="text-green-600 hover:text-green-800"
                            title={isOnline ? 'Open navigation (Google Maps)' : 'Offline'}
                          >
                            <SiGooglemaps size={18} />
                          </button>
                          <button
                            onClick={() => openNotesHistoryModal(v.student_id, studentName)}
                            className="text-purple-700 hover:text-purple-900"
                            title={isOnline ? 'Show Notes History' : 'Offline'}
                          >
                            <FaBookOpen size={18} />
                          </button>

                          {(() => {
                            const disabledTrip = !canStartTripForVisit(v, serverDateStr);
                            const showAiGuidance = canShowAiGuidanceForVisit(v);
                            const guidanceIconClass = aiAccess.loading
                              ? 'text-gray-300 cursor-not-allowed'
                              : canUseAiGuidance
                              ? 'text-sky-600 hover:text-sky-800'
                              : 'text-amber-600 hover:text-amber-800';

                            const guidanceTitle = aiAccess.loading
                              ? 'Checking AI access...'
                              : canUseAiGuidance
                              ? 'AI visit guidance'
                              : getAiDisabledMessage('visit_guidance');

                            return (
                              <>
                                <button
                                  onClick={async () => {
                                    if (disabledTrip) return;
                                    try {
                                      const { lat, lon, label } = await getVisitDestination(v);

                                      const okToSwitch = await confirmSwitchTripIfNeeded({
                                        dest_kind: 'visit',
                                        dest_id: v.id,
                                        destLabel: label,
                                      });
                                      if (!okToSwitch) return;

                                      const okStart = await confirmStartTrip({ label });
                                      if (!okStart) return;

                                      await startTrip({
                                        dest_kind: 'visit',
                                        dest_id: v.id,
                                        destLabel: label,
                                        destLat: lat,
                                        destLon: lon,
                                      });
                                    } catch {
                                      alert('Destination is missing coordinates.');
                                    }
                                  }}
                                  disabled={disabledTrip}
                                  className={`ml-1 ${
                                    disabledTrip ? 'text-gray-300 cursor-not-allowed' : 'text-indigo-700 hover:text-indigo-900'
                                  }`}
                                  title={disabledTrip ? 'Trip tracking disabled for completed/canceled visit' : 'Start trip tracking'}
                                >
                                  <Car size={20} />
                                </button>

                                {showAiGuidance ? (
                                  <button
                                    onClick={() => openAiGuidanceModal(v)}
                                    className={guidanceIconClass}
                                    title={guidanceTitle}
                                  >
                                    <Sparkles size={18} />
                                  </button>
                                ) : null}
                              </>
                            );
                          })()}

                          <button
                            onClick={() => openReminderModal(v.student_id, studentName)}
                            className={`${
                              reminderExists ? 'text-yellow-600 hover:text-yellow-800' : 'text-gray-400 hover:text-gray-600'
                            }`}
                            title={reminderExists ? 'Edit student reminder' : 'Add student reminder'}
                          >
                            {reminderExists ? <FaStickyNote size={18} /> : <FaRegStickyNote size={18} />}
                          </button>
                        </div>

                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <StatusBadge status={v.status}>{v.status === 'canceled' ? 'Canceled' : v.status}</StatusBadge>

                          {showPendingBadge ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                              Pending sync ({pendingCount})
                            </span>
                          ) : null}

                          <TelehealthBadge delivery_mode={v.delivery_mode} />
                          {isTrainingVisitForCurrentTeacher && <TrainingBadge trainingType={trainingType} />}
                        </div>

                        {isSupervisorVisitForCurrentTeacher && v.trainee_teacher_id && (
                          <button
                            type="button"
                            onClick={() => setMentorFeedback({ open: true, visit: v })}
                            className="mt-2 inline-flex px-2 py-0.5 rounded text-[11px] bg-blue-50 text-blue-700 border border-blue-300 hover:bg-blue-100"
                          >
                            Mentor feedback
                          </button>
                        )}

                        {isCanceled && v.notes && <div className="mt-2 text-xs text-purple-700 italic">{v.notes}</div>}

                        {reassigned && status && !isCanceled && (
                          <div className={`mt-2 text-xs ${status === 'rejected' ? 'text-red-700' : 'text-orange-700'}`}>
                            Substitute: {status}
                          </div>
                        )}

                        {reminderExists && reminderPreview ? (
                          <div className="mt-2 text-[11px] text-yellow-800 bg-yellow-50 border border-yellow-200 rounded px-2 py-1 inline-block">
                            <span className="font-semibold">Reminder:</span> {reminderPreview}
                          </div>
                        ) : null}
                      </div>

                      <div className="shrink-0 text-right">
                        <VisitTimeType scheduled_time={v.scheduled_time} event_end_time={v.event_end_time} />
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2 items-start">
                      <div className="min-w-0">
                        <div className="text-xs text-slate-600">
                          <div className="font-semibold text-slate-700 mb-1">Check-in</div>
                          <div className="min-w-0">{checkinContent}</div>
                        </div>

                        <div className="mt-3 text-xs text-slate-600">
                          <div className="font-semibold text-slate-700 mb-1">G-sign</div>
                          <div className="text-base leading-none">{gsignContent}</div>
                        </div>
                      </div>

                      <div className="min-w-0">
                        <div className="text-xs text-slate-600">
                          <div className="font-semibold text-slate-700 mb-1">Checkout</div>
                          <div className="min-w-0">{checkoutContent || <span className="text-xs text-slate-300">—</span>}</div>
                        </div>

                        {!hideActionsForRole ? (
                          <div className="mt-2 flex flex-col gap-2">
                            {canEditNotes && (
                              <button
                                onClick={() => openNotesModal(v, 'edit')}
                                disabled={offlineBlocked}
                                className={`${btnBase} ${offlineBlocked ? btnDisabled : isOnline ? btnYellow : btnAmber}`}
                              >
                                {offlineBlocked ? 'Offline' : 'Edit Notes'}
                              </button>
                            )}

                            {canShowQR && (
                              <button
                                onClick={() => {
                                  setQrModalVisitId(v.id);
                                  setVisitConfirmed(false);
                                }}
                                className={`${btnBase} ${btnGray}`}
                                disabled={!isOnline}
                                title={!isOnline ? 'Offline: QR status cannot be refreshed' : 'Show QR'}
                              >
                                Show QR
                              </button>
                            )}

                            {canCancel(v) && (
                              <button
                                onClick={() => setCancelVisitId(v.id)}
                                disabled={offlineBlocked}
                                className={`${btnBase} ${offlineBlocked ? btnDisabled : isOnline ? btnPurple : btnAmber}`}
                              >
                                {offlineBlocked ? 'Offline' : isOnline ? 'Cancel' : 'Cancel (sync later)'}
                              </button>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      <CancelModal
        open={!!cancelVisitId}
        onClose={() => setCancelVisitId(null)}
        onSubmit={handleCancelVisit}
        loading={cancelLoading}
      />

      <CancelWorkModal
        open={!!cancelWorkId}
        onClose={() => setCancelWorkId(null)}
        onSubmit={handleCancelWork}
        loading={cancelWorkLoading}
      />

      <WorkLocationModal
        open={!!workLocModal?.open}
        onClose={() => setWorkLocModal({ open: false, title: '', addressLine: '', dest: null, teacher: null })}
        title={workLocModal?.title}
        addressLine={workLocModal?.addressLine}
        dest={workLocModal?.dest}
        teacher={workLocModal?.teacher}
      />

      <WorkNotesModal
        open={!!workNotesModal?.open}
        onClose={() => setWorkNotesModal((s) => ({ ...(s || {}), open: false }))}
        data={workNotesModal}
      />

      {showNotesModal && notesModalVisit ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[85vh] overflow-y-auto p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-lg font-bold">{notesEditMode ? 'Edit notes' : 'Checkout notes'}</h3>
                <p className="text-sm text-gray-600">
                  {(notesModalVisit.student_last_name || '') +
                    (notesModalVisit.student_last_name ? ', ' : '') +
                    (notesModalVisit.student_first_name || '')}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowNotesModal(false);
                  setNotesModalVisit(null);
                  resetAiSuggestionState();
                }}
                className="text-gray-500 hover:text-gray-800"
                title="Close"
                disabled={notesSaving}
              >
                ✕
              </button>
            </div>

            {notesModalLoading ? (
              <div className="p-4 text-center text-gray-400 text-sm">Loading domains…</div>
            ) : (
              <>
                <AiVisitSuggestionPanel
                  loading={aiNoteLoading}
                  error={aiNoteError}
                  result={aiNoteResult}
                  onGenerate={generateAiVisitSuggestion}
                  onCopyShort={() => handleCopyAiField('short')}
                  onCopyDetailed={() => handleCopyAiField('detailed')}
                  onApplyHighlights={applyAiHighlightsToDomainNotes}
                  copiedField={aiCopiedField}
                  applyMessage={aiApplyMessage}
                  disabled={!isOnline}
                  generateLocked={aiNoteGeneratedOnce}
                  aiNotesEnabled={canUseAiNotes}
                  aiAccessLoading={aiAccess.loading}
                  disabledReason={getAiDisabledMessage('visit_note')}
                />

                {!domainList.length ? (
                  <div className="text-sm text-gray-500">No domains available.</div>
                ) : (
                  <div className="space-y-3">
                    {domainList.map((d) => (
                      <div key={d.id} className="border rounded-lg p-3">
                        <div className="text-sm font-semibold text-slate-800 mb-2">{d.name}</div>
                        <textarea
                          className="w-full border rounded px-3 py-2 text-sm"
                          rows={3}
                          value={domainNotes?.[d.id] ?? ''}
                          onChange={(e) =>
                            setDomainNotes((prev) => ({
                              ...(prev || {}),
                              [d.id]: e.target.value,
                            }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => {
                      setShowNotesModal(false);
                      setNotesModalVisit(null);
                      resetAiSuggestionState();
                    }}
                    className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
                    disabled={notesSaving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNotes}
                    className={`px-4 py-2 text-sm rounded text-white ${
                      notesSaving ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                    disabled={notesSaving}
                  >
                    {notesSaving ? 'Saving…' : notesEditMode ? 'Save notes' : 'Save & checkout'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {qrModalVisitId ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6 text-center">
            <h3 className="text-lg font-bold mb-2">G-sign QR</h3>
            <p className="text-sm text-gray-600 mb-4">Ask parent to scan the QR to confirm the visit.</p>

            <div className="flex items-center justify-center mb-4">
              <QRCode value={`${window.location.origin}/visit/${qrModalVisitId}/confirm`} size={220} />
            </div>

            {visitConfirmed ? (
              <div className="text-green-700 font-semibold mb-3">Confirmed ✅</div>
            ) : (
              <div className="text-gray-500 text-sm mb-3">Waiting for confirmation…</div>
            )}

            <button onClick={handleCloseQRModal} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
              Close
            </button>
          </div>
        </div>
      ) : null}

      {showNotesHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold">Notes History: {notesHistoryStudent}</h3>
              <button className="text-gray-500" onClick={() => setShowNotesHistory(false)}>
                ✕
              </button>
            </div>
            {notesHistoryLoading ? (
              <div className="p-4 text-center text-gray-400">Loading...</div>
            ) : notesHistory.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No notes found for this student.</div>
            ) : (
              <div className="space-y-4">
                {notesHistory.map((entry, idx) => (
                  <div key={idx} className="border rounded p-3 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex flex-col">
                        <span className="font-semibold text-blue-900">{entry.teacher_name}</span>
                        {entry.discipline_name && <span className="text-xs text-gray-500">{entry.discipline_name}</span>}
                      </div>
                      <span className="text-xs text-gray-500">
                        {entry.scheduled_at ? entry.scheduled_at.slice(0, 16).replace('T', ' ') : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {entry.domains && entry.domains.length > 0 ? (
                        entry.domains.map((dom) =>
                          dom.note && dom.note.trim() !== '' ? (
                            <div key={dom.domain_id}>
                              <span className="font-semibold">{dom.name || dom.code || 'Domain'}:</span>{' '}
                              <span>{dom.note}</span>
                            </div>
                          ) : null
                        )
                      ) : (
                        <span className="text-xs text-gray-500 italic">No domain notes for this visit.</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <StudentReminderModal
        open={!!reminderModal?.open}
        onClose={closeReminderModal}
        studentName={reminderModal?.student_name || ''}
        value={reminderValue}
        setValue={setReminderValue}
        meta={reminderMeta}
        loading={reminderLoading}
        saving={reminderSaving}
        onSave={saveReminder}
        offlineHint={!isOnline}
        offlineQueueEnabled={offlineQueueEnabled}
      />

      <MentorFeedbackModal
        open={!!mentorFeedback?.open}
        onClose={() => setMentorFeedback({ open: false, visit: null })}
        visit={mentorFeedback?.visit}
        offlineHint={!isOnline}
        onSaveOffline={queueMentorFeedback}
        offlineQueueEnabled={offlineQueueEnabled}
      />

      <AiVisitGuidanceModal
        open={!!aiGuidanceModal?.open}
        onClose={closeAiGuidanceModal}
        loading={!!aiGuidanceModal?.loading}
        error={aiGuidanceModal?.error || ''}
        visit={aiGuidanceModal?.visit}
        data={aiGuidanceModal?.data}
      />

      <StudentDetailsModal
        open={!!studentDetailsModal?.open}
        student={studentDetailsModal?.student}
        onClose={() => setStudentDetailsModal({ open: false, student: null })}
      />

      <MapModal
        open={!!mapModal?.open}
        student={mapModal?.student}
        teacher={mapModal?.teacher}
        onClose={() => setMapModal({ open: false, student: null, teacher: null })}
      />
    </div>
  );
}

export default MyVisitsToday;












